import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Lottie from 'lottie-react';

import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import TeamFilterPanel, { TeamFilterItem } from '../../../../components/tickets/components/TeamFilterPanel';
import TeamTimelineCard, { type TeamTimelineLane } from '../../../../components/tickets/components/TeamTimelineCard';
import TicketsTable from '../../../../components/tickets/components/TicketsTable';
import TicketEditorDrawer from '../../../../components/tickets/components/TicketEditorDrawer';
import CreateProjectModal from '../../../../components/tickets/components/CreateProjectModal';
import ProjectDetailsDrawer, {
  buildProjectStatusSummary,
  type ProjectCapacityForecastContext,
} from '../../../../components/tickets/components/ProjectDetailsDrawer';
import GlassPicklist, { type GlassPicklistOption } from '../../../../components/ui/GlassPicklist';
import { buildTicketEditorTicket } from '../../../../lib/tickets/ticketEditorTicket';

import { useApi } from '../../../../context/ApiProvider';
import { useAuth } from '../../../../context/AuthProvider';
import { useUsers } from '../../../../hooks/useUsers';
import spacemailAnimation from '../../../../assets/spacemail.json';
import { resolveClientDisplayName } from '../../../../lib/clientDisplayName';
import {
  normalizeTeamCapacitySummaryPayload,
  type TeamCapacitySummary,
} from '../../../../lib/projects/teamCapacitySummary';
import { gradientPillCss } from '../../../../styles/gradientPill';
import { normalizeServiceOfferingCatalog } from '../../../../lib/services/offeringSla';
import { resolveInternalAssigneeDisplay } from '../../../../lib/ticketAssigneeDisplay';
import { convertFromUTC, getUserTimezone } from '../../../../utils/timezone';

import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../../store';
import {
  fetchProjects,
  createProject,
  deleteProject,
  updateProject,
  duplicateProject,
  createProjectMilestone,
  updateProjectMilestone,
  deleteProjectMilestone,
  selectAllProjects,
  type Project,
} from '../../../../store/features/projects/projectsSlice';
import {
  fetchTickets,
  createTicket as createTicketThunk,
  selectAllTickets,
  selectCachedTicketById,
  selectListLoading,
  assignTicket,
  updateTicketStatus,
} from '../../../../store/features/tickets/ticketsSlice';
import { openTicketPortal } from '../../../../store/features/ticketPortal/ticketPortalSlice';
import type { Ticket } from '../../../../store/features/tickets/types';
import { getSubscription } from '../../../../store/features/billing/billingSlice';

type UiStatus = 'submitted' | 'triage' | 'in_progress' | 'blocked' | 'delivered' | 'closed';
type UiPriority = 'urgent' | 'high' | 'medium' | 'low';

type UiTicket = {
  id: string;
  orgId?: string | null;
  ticketNumber?: string | null;
  title: string;
  projectId: string;
  milestoneId?: string | null;
  milestoneTitle?: string | null;
  clientId: string;
  clientBusinessName?: string | null;
  clientFirstName?: string | null;
  clientLastName?: string | null;
  clientDisplayName?: string | null;
  clientEmail?: string | null;
  createdBy?: string | null;
  createdByFirstName?: string | null;
  createdByLastName?: string | null;
  createdByDisplayName?: string | null;
  createdByEmail?: string | null;
  status: UiStatus;
  priority: UiPriority;
  timelineSortOrder: number;
  teamId?: string | null;
  owningTeamNodeId?: string | null;
  currentTeamNodeId?: string | null;
  defaultTeamNodeId?: string | null;
  currentTeamLabel?: string | null;
  defaultTeamLabel?: string | null;
  assignedAgentId?: string;
  assignedAgentDisplayName?: string | null;
  assignedAgentFirstName?: string | null;
  assignedAgentLastName?: string | null;
  assignedAgentEmail?: string | null;
  offeringCode?: string | null;
  offeringName?: string | null;
  offeringSubcategoryId?: string | null;
  offeringSubcategoryName?: string | null;
  startAt: string;
  endAt: string;
  updatedAt: string;
};

type TeamAssignmentRow = {
  clientUid: string;
  clientUserId: string;
  clientName: string;
  clientEmail: string;
  teamNodeId: string;
  teamLabel: string;
  designationType: string;
  owner: string;
  sourceLevel: 'tier' | 'service';
  serviceId: string;
  serviceCode: string;
  serviceTitle: string;
  tierId: string | null;
  tierSkuId: string | null;
  tierName: string | null;
};

type ProjectMilestoneComposer = {
  position: 'top' | 'bottom' | 'empty';
  value: string;
};

type DeleteMilestoneDialogState = {
  projectId: string;
  milestoneId: string;
  milestoneTitle: string;
  childTicketCount: number;
};

type DeleteProjectDialogState = {
  projectId: string;
  projectTitle: string;
  childTicketCount: number;
};

type DuplicateProjectDialogState = {
  projectId: string;
  projectLabel: string;
};

type OpenMilestoneMenuState = {
  projectId: string;
  milestoneId: string;
  milestoneTitle: string;
  childTicketCount: number;
  left: number;
  top: number;
  placement: 'top' | 'bottom';
};

type CatalogServiceEstimateRecord = {
  id: string;
  code?: string | null;
  metadata?: Record<string, unknown> | null;
};

type ForecastServiceFamily = 'marketing' | 'platform' | 'assets_design';

type ProjectForecastAccountHealth = {
  contractedMonthlyMinutesMin: number | null;
  contractedMonthlyMinutesMax: number | null;
  remainingBillableMinutesMin: number | null;
  remainingBillableMinutesMax: number | null;
  plannedRemainingEstimateMinutes: number;
};

type ProjectForecastServiceContract = {
  id: string;
  serviceCode: string | null;
  serviceName: string | null;
  primaryTeamLabels: string[];
  contractedMonthlyMinutesMin: number | null;
  contractedMonthlyMinutesMax: number | null;
  remainingBillableMinutesMin: number | null;
  remainingBillableMinutesMax: number | null;
  plannedRemainingEstimateMinutes: number;
};

type ProjectForecastOrgDetails = {
  accountHealth: ProjectForecastAccountHealth | null;
  serviceContracts: ProjectForecastServiceContract[];
};

const Shell = styled.section`
  display: grid;
  gap: 0;
  grid-template-columns: 1fr;
  min-width: 0;
  height: calc(100dvh - 2.5rem);
  @supports not (height: 100dvh) {
    height: calc(100vh - 2.5rem);
  }
`;

const WorkspaceGateCard = styled(Card)`
  width: min(100%, 720px);
  margin: auto;
  padding: 0;
  display: grid;
  gap: 0;
`;

const WorkspaceGateHero = styled.div`
  position: relative;
  min-height: 240px;
  padding: 22px 24px;
  display: grid;
  place-items: center;
  overflow: hidden;
  background:
    radial-gradient(circle at 22% 20%, rgba(255, 176, 232, 0.26), transparent 34%),
    radial-gradient(circle at 82% 24%, rgba(125, 149, 255, 0.22), transparent 38%),
    linear-gradient(145deg, #6b34f2 0%, #4a1fa6 52%, #241447 100%);
`;

const WorkspaceGateHeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(10, 7, 25, 0.08) 0%, rgba(10, 7, 25, 0.36) 100%),
    radial-gradient(circle at center, rgba(255, 255, 255, 0.04), transparent 58%);
  pointer-events: none;
`;

const WorkspaceGateLottieWrap = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  opacity: 0.94;
  pointer-events: none;

  & > div {
    width: min(100%, 430px);
    height: 190px;
    transform: translateY(-4px);
  }
`;

const WorkspaceGateHeroTitle = styled.h2`
  position: relative;
  z-index: 2;
  margin: 0;
  max-width: 420px;
  color: #fff7ff;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: clamp(1.05rem, 2.2vw, 1.45rem);
  line-height: 1.2;
  font-weight: 900;
  text-shadow: 0 10px 28px rgba(13, 8, 26, 0.34);
`;

const WorkspaceGateBottom = styled.div`
  padding: 22px 24px 24px;
  display: grid;
  gap: 14px;
`;

const WorkspaceGateTitle = styled.h2`
  margin: 0;
  color: #f5f7fb;
  font-size: 1.1rem;
  font-weight: 800;
`;

const WorkspaceGateBody = styled.p`
  margin: 0;
  color: #aeb6c2;
  line-height: 1.6;
`;

const WorkspaceGateActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const WorkspaceGateCta = styled(Button)`
  ${gradientPillCss};
  border: none;
  width: auto;
  min-width: 0;
`;

const Layout = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
  height: 100%;
  min-height: 0;
  @media (min-width: 900px) {
    grid-template-rows: minmax(0, 1.15fr) minmax(0, 0.85fr);
    align-items: stretch;
  }
  @media (max-width: 899px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 12px;
  }
`;

const TopRow = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
  min-height: 0;
  @media (min-width: 900px) {
    grid-template-columns: clamp(240px, 22vw, 300px) minmax(0, 1fr);
  }
  @media (max-width: 899px) {
    grid-template-columns: 1fr;
    grid-auto-rows: minmax(0, auto);
  }
`;

const BottomRow = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
  min-height: 0;
  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 30%) minmax(0, 70%);
  }
  @media (max-width: 899px) {
    grid-template-columns: 1fr;
    grid-auto-rows: minmax(0, auto);
  }
`;

const ClientsArea = styled.div<{ $showTeamPanel: boolean }>`
  display: grid;
  grid-template-rows: ${({ $showTeamPanel }) =>
    $showTeamPanel ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  gap: 10px;
  min-width: 0;
  min-height: 0;
`;

const TeamPanel = styled(Card)`
  padding: 10px;
  display: grid;
  gap: 8px;
`;

const TeamPicklistControl = styled.div`
  min-height: 38px;

  & > div {
    height: 100%;
  }

  & > div > button {
    min-height: 38px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, .14) !important;
    background: rgba(255, 255, 255, .04);
    padding: 0 30px 0 11px;
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & > div > button:hover {
    border-color: rgba(224, 161, 94, .55) !important;
  }

  & > div > button:focus,
  & > div > button:focus-visible {
    border-color: rgba(224, 161, 94, .75) !important;
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }

  & > div > span {
    right: 10px;
    font-size: .66rem;
  }
`;

const TeamPanelTitle = styled.div`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .28px;
  text-transform: uppercase;
  color: #E8EAED;
`;

const ClientsPanelWrap = styled.div`
  min-height: 0;
  height: 100%;
  & > * {
    min-height: 0;
    height: 100%;
  }
`;

const TimelineArea = styled.div`
  min-width: 0;
  min-height: 0;
`;

const ProjectsArea = styled.div`
  display: grid;
  min-width: 0;
  min-height: 0;
  & > * {
    min-height: 0;
    height: 100%;
  }
`;

const TableArea = styled.div`
  min-width: 0;
  min-height: 0;
`;

const MobileWorkspaceLayout = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 12px;
  min-width: 0;
  min-height: 0;
  height: 100%;
`;

const MobileFiltersCard = styled(Card)`
  padding: 12px;
  display: grid;
  gap: 12px;
`;

const MobileFiltersGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const MobileFilterField = styled.div`
  display: grid;
  gap: 6px;
`;

const MobileFilterLabel = styled.div`
  color: #e0a15e;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
`;

const MobilePicklistControl = styled.div`
  min-height: 42px;

  & > div {
    height: 100%;
  }

  & > div > button {
    min-height: 42px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, .14) !important;
    background: rgba(255, 255, 255, .04);
    padding: 0 34px 0 12px;
    font-size: 13px;
    font-weight: 800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & > div > button:hover {
    border-color: rgba(224, 161, 94, .55) !important;
  }

  & > div > button:focus,
  & > div > button:focus-visible {
    border-color: rgba(224, 161, 94, .75) !important;
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }

  & > div > span {
    right: 11px;
    font-size: .68rem;
  }
`;

const MobileTicketsArea = styled.div`
  min-width: 0;
  min-height: 0;
  height: 100%;

  & > * {
    min-height: 0;
    height: 100%;
  }
`;

const ProjectMilestoneAccordion = styled.div`
  display: grid;
  gap: 10px;
  padding-right: 4px;
  justify-items: end;
  text-align: right;
`;

const ProjectMilestoneAction = styled.button`
  justify-self: end;
  border: 0;
  background: transparent;
  color: #E8EAED;
  padding: 0;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .08em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: #f3d3a2;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    color: #f3d3a2;
  }
`;

const ProjectMilestoneEmpty = styled.button`
  justify-self: end;
  border: 0;
  background: transparent;
  color: #9aa4b2;
  padding: 0;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    color: #E8EAED;
  }
`;

const ProjectMilestoneList = styled.div`
  display: grid;
  gap: 8px;
  justify-items: end;
  width: 100%;
`;

const ProjectMilestoneRow = styled.div<{ $active: boolean }>`
  display: grid;
  gap: 8px;
  width: 75%;
  max-width: 100%;
  min-width: 320px;
  min-height: 34px;
  padding: 8px 12px;
  border: 1px dashed ${({ $active }) => ($active ? 'rgba(224, 161, 94, .62)' : 'rgba(255, 255, 255, .18)')};
  border-radius: 12px;
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, .08)' : 'transparent')};
  align-content: center;
  cursor: pointer;
  position: relative;

  &:hover {
    border-color: rgba(224, 161, 94, .42);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .72);
    box-shadow: inset 0 0 0 1px rgba(224, 161, 94, .18);
  }

  @media (max-width: 899px) {
    width: 100%;
    min-width: 0;
  }
`;

const ProjectMilestoneTitle = styled.div`
  color: #E8EAED;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
  text-align: left;
`;

const ProjectMilestoneHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const ProjectMilestoneHeaderBody = styled.div`
  min-width: 0;
  display: grid;
  gap: 4px;
`;

const ProjectMilestoneMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  text-align: left;
  flex-wrap: wrap;
`;

const ProjectMilestoneDateTrigger = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .04em;
  text-align: left;
  outline: none;
  box-shadow: none;
  cursor: pointer;

  &:focus,
  &:focus-visible {
    color: #E8EAED;
  }

  &:hover {
    color: #E8EAED;
  }
`;

const ProjectMilestoneDateSeparator = styled.span`
  color: rgba(255, 255, 255, .26);
  font-size: 11px;
  font-weight: 800;
`;

const ProjectMilestoneActions = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

const ProjectMilestoneMenuButton = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, .12);
  background: rgba(255, 255, 255, .04);
  color: #cbd2e1;
  display: inline-grid;
  place-items: center;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: #E8EAED;
    border-color: rgba(224, 161, 94, .38);
    background: rgba(224, 161, 94, .08);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    color: #E8EAED;
    border-color: rgba(224, 161, 94, .68);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .12);
  }
`;

const ProjectMilestoneMenu = styled.div<{ $left: number; $top: number; $placement: 'top' | 'bottom' }>`
  position: fixed;
  left: ${({ $left }) => `${$left}px`};
  top: ${({ $top }) => `${$top}px`};
  min-width: 160px;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, .12);
  background: #171a22;
  box-shadow: 0 16px 32px rgba(0, 0, 0, .34);
  z-index: 2000;
  display: grid;
  gap: 4px;
  transform: ${({ $placement }) =>
    $placement === 'top' ? 'translate(-100%, -100%)' : 'translate(-100%, 0)'};
`;

const ProjectMilestoneMenuItem = styled.button<{ $danger?: boolean }>`
  border: 0;
  border-radius: 8px;
  padding: 10px 12px;
  background: transparent;
  color: ${({ $danger }) => ($danger ? '#ffb4b4' : '#E8EAED')};
  font-size: 12px;
  font-weight: 800;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ $danger }) => ($danger ? 'rgba(255, 118, 118, .08)' : 'rgba(255, 255, 255, .05)')};
  }
`;

const HiddenMilestoneDateInput = styled.input.attrs({ type: 'date' })`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`;

const ProjectMilestoneInput = styled.input`
  width: min(420px, 100%);
  min-height: 34px;
  padding: 0 0 0 2px;
  border: 1px dashed rgba(255, 255, 255, .22);
  border-radius: 10px;
  background: transparent;
  color: #E8EAED;
  font-size: 12px;
  font-weight: 800;
  outline: none;
  box-shadow: none;

  &::placeholder {
    color: #6b7280;
  }

  &:focus,
  &:focus-visible {
    border-color: rgba(224, 161, 94, .6);
  }
`;

function resolveMilestoneMenuPosition(rect: DOMRect): OpenMilestoneMenuState['placement'] {
  return rect.top >= 96 ? 'top' : 'bottom';
}

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8, 10, 14, .78);
  display: grid;
  place-items: center;
  z-index: 1400;
  padding: 24px;
`;

const ConfirmDialog = styled(Card)`
  width: min(520px, calc(100vw - 32px));
  padding: 18px;
  display: grid;
  gap: 16px;
`;

const ConfirmHeader = styled.div`
  display: grid;
  gap: 4px;
`;

const ConfirmTitle = styled.h3`
  margin: 0;
  color: #E8EAED;
  font-size: 1rem;
  font-weight: 900;
  letter-spacing: .02em;
`;

const ConfirmBody = styled.div`
  color: #cbd2e1;
  font-size: 13px;
  line-height: 1.55;
`;

const ConfirmOptions = styled.div`
  display: grid;
  gap: 10px;
`;

const ConfirmOption = styled.button<{ $active: boolean; $danger?: boolean }>`
  border: 1px solid ${({ $active, $danger }) =>
    $active
      ? ($danger ? 'rgba(255, 118, 118, .52)' : 'rgba(224, 161, 94, .52)')
      : 'rgba(255, 255, 255, .12)'};
  border-radius: 12px;
  background: ${({ $active, $danger }) =>
    $active
      ? ($danger ? 'rgba(255, 118, 118, .08)' : 'rgba(224, 161, 94, .08)')
      : 'rgba(255, 255, 255, .03)'};
  padding: 12px 14px;
  color: #E8EAED;
  display: grid;
  gap: 3px;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: ${({ $danger }) => ($danger ? 'rgba(255, 118, 118, .42)' : 'rgba(224, 161, 94, .42)')};
  }
`;

const ConfirmOptionTitle = styled.div`
  font-size: 12px;
  font-weight: 900;
`;

const ConfirmOptionMeta = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 700;
`;

const ConfirmInputLabel = styled.label`
  display: grid;
  gap: 8px;
  color: #cbd2e1;
  font-size: 12px;
  font-weight: 800;
`;

const ConfirmInput = styled.input`
  width: 100%;
  min-height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, .04);
  padding: 0 12px;
  color: #E8EAED;
  font-size: 13px;
  font-weight: 700;
  outline: none;

  &::placeholder {
    color: #6b7280;
  }

  &:focus,
  &:focus-visible {
    border-color: rgba(224, 161, 94, .7);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .12);
  }
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ConfirmButton = styled.button<{ $danger?: boolean }>`
  min-height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid ${({ $danger }) => ($danger ? 'rgba(255, 118, 118, .36)' : 'rgba(255, 255, 255, .14)')};
  background: ${({ $danger }) => ($danger ? 'rgba(255, 118, 118, .12)' : 'rgba(255, 255, 255, .04)')};
  color: ${({ $danger }) => ($danger ? '#ffd8d8' : '#E8EAED')};
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: .45;
    cursor: not-allowed;
  }
`;

function tsToIso(v: any): string {
  if (!v) return new Date().toISOString();
  if (typeof v === 'string') return v;
  const sec = v.seconds ?? v._seconds;
  if (typeof sec === 'number') return new Date(sec * 1000).toISOString();
  if (v.toDate) return (v.toDate() as Date).toISOString();
  return new Date().toISOString();
}

function maybeTsToIso(v: any): string | null {
  if (!v) return null;
  if (typeof v === 'string') {
    const parsed = new Date(v);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  const sec = v.seconds ?? v._seconds;
  if (typeof sec === 'number') {
    const parsed = new Date(sec * 1000);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  if (typeof v?.toDate === 'function') {
    const parsed = v.toDate() as Date;
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : v.toISOString();
  }
  return null;
}

function formatMilestoneDateLabel(
  value: string | null | undefined,
  timezone: string | null | undefined,
  placeholder: string,
): string {
  if (typeof value !== 'string' || !value.trim()) return placeholder;
  try {
    const { localDate } = convertFromUTC(new Date(value), timezone || 'UTC');
    const [, month, day] = localDate.split('-');
    if (!month || !day) return placeholder;
    return `${month}/${day}`;
  } catch {
    return placeholder;
  }
}

function mapStatusToUi(s: string): UiStatus {
  switch (s) {
    case 'in_progress': return 'in_progress';
    case 'blocked': return 'blocked';
    case 'resolved': return 'delivered';
    case 'closed': return 'closed';
    case 'open':
    default: return 'submitted';
  }
}
function mapPriorityToUi(p?: string): UiPriority {
  switch (p) {
    case 'P0': return 'urgent';
    case 'P1': return 'high';
    case 'P3': return 'low';
    case 'P2':
    default: return 'medium';
  }
}
function mapPriorityToApi(p: UiPriority): 'P0' | 'P1' | 'P2' | 'P3' {
  return p === 'urgent' ? 'P0' : p === 'high' ? 'P1' : p === 'low' ? 'P3' : 'P2';
}

function toLocalDateInputFromIso(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toLocalDateInputFromIsoInTimezone(
  iso: string | null | undefined,
  timezone: string | null | undefined,
): string {
  if (typeof iso !== 'string' || !iso.trim()) return '';
  try {
    return convertFromUTC(new Date(iso), timezone || 'UTC').localDate;
  } catch {
    return '';
  }
}

function arraysEqual(a: readonly string[], b: readonly string[]) {
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
}

function normalizeNonNegativeInt(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.round(parsed);
}

function parseOptionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function safeText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toProjectTimelineRange(project: Project, tickets: readonly Ticket[]) {
  const startCandidates: string[] = [];
  const endCandidates: string[] = [];

  const pushStart = (value: unknown) => {
    const iso = maybeTsToIso(value);
    if (iso) startCandidates.push(iso);
  };
  const pushEnd = (value: unknown) => {
    const iso = maybeTsToIso(value);
    if (iso) endCandidates.push(iso);
  };

  pushStart(project.plannedStartAt);
  pushEnd(project.plannedEndAt);

  const milestones = Array.isArray(project.milestones) ? project.milestones : [];
  for (const milestone of milestones) {
    pushStart(milestone.plannedStartAt);
    pushEnd(milestone.plannedEndAt);
  }

  for (const ticket of tickets) {
    pushStart((ticket as any).plannedStartAt ?? ticket.createdAt ?? ticket.updatedAt);
    pushEnd((ticket as any).plannedEndAt ?? (ticket as any).plannedStartAt ?? ticket.updatedAt ?? ticket.createdAt);
  }

  const sortAscending = (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime();
  const startAt =
    [...startCandidates].sort(sortAscending)[0] ??
    maybeTsToIso(project.createdAt) ??
    maybeTsToIso(project.updatedAt) ??
    new Date().toISOString();

  const fallbackEnd =
    [...endCandidates].sort(sortAscending).at(-1) ??
    [...startCandidates].sort(sortAscending).at(-1) ??
    maybeTsToIso(project.updatedAt) ??
    maybeTsToIso(project.createdAt) ??
    startAt;

  const startMs = new Date(startAt).getTime();
  const fallbackEndMs = new Date(fallbackEnd).getTime();
  const endAt =
    Number.isFinite(startMs) && Number.isFinite(fallbackEndMs) && fallbackEndMs >= startMs
      ? new Date(fallbackEndMs).toISOString()
      : new Date(startMs + 24 * 60 * 60 * 1000).toISOString();

  return { startAt, endAt };
}

function toProjectTimelineStatus(
  project: Project,
  statusSummary: ReturnType<typeof buildProjectStatusSummary>,
  range: { startAt: string; endAt: string },
) {
  if (statusSummary.blockedTickets > 0) return 'blocked';
  if (statusSummary.totalTickets > 0 && statusSummary.remainingTickets === 0) return 'delivered';
  const startMs = new Date(range.startAt).getTime();
  if (Number.isFinite(startMs) && startMs <= Date.now()) {
    if (statusSummary.totalTickets > 0 || statusSummary.totalMilestones > 0) return 'in_progress';
  }
  if (statusSummary.totalTickets > 0 || statusSummary.totalMilestones > 0) return 'submitted';
  if (maybeTsToIso(project.plannedStartAt)) return 'open';
  return 'triage';
}

function normalizeForecastServiceFamily(value: unknown): ForecastServiceFamily | null {
  const normalized = safeText(value).toLowerCase();
  if (!normalized) return null;
  if (normalized === 'marketing' || normalized === 'svc_marketing') return 'marketing';
  if (normalized === 'platform' || normalized === 'svc_platform') return 'platform';
  if (
    normalized === 'assets_design' ||
    normalized === 'assets/design' ||
    normalized === 'svc_assets_design'
  ) {
    return 'assets_design';
  }
  return null;
}

function inferForecastServiceFamilyFromContract(
  contract: ProjectForecastServiceContract,
): ForecastServiceFamily | null {
  const direct = normalizeForecastServiceFamily(contract.serviceCode);
  if (direct) return direct;
  const haystack = `${contract.serviceName ?? ''} ${contract.primaryTeamLabels.join(' ')}`.toLowerCase();
  if (haystack.includes('marketing')) return 'marketing';
  if (haystack.includes('platform') || haystack.includes('development')) return 'platform';
  if (haystack.includes('asset') || haystack.includes('design')) return 'assets_design';
  return null;
}

function normalizeProjectForecastOrgDetails(raw: unknown): ProjectForecastOrgDetails | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const rawAccountHealth =
    record.accountHealth && typeof record.accountHealth === 'object' && !Array.isArray(record.accountHealth)
      ? (record.accountHealth as Record<string, unknown>)
      : null;
  const accountHealth = rawAccountHealth
    ? {
        contractedMonthlyMinutesMin: parseOptionalNumber(rawAccountHealth.contractedMonthlyMinutesMin),
        contractedMonthlyMinutesMax: parseOptionalNumber(rawAccountHealth.contractedMonthlyMinutesMax),
        remainingBillableMinutesMin: parseOptionalNumber(rawAccountHealth.remainingBillableMinutesMin),
        remainingBillableMinutesMax: parseOptionalNumber(rawAccountHealth.remainingBillableMinutesMax),
        plannedRemainingEstimateMinutes: Number(rawAccountHealth.plannedRemainingEstimateMinutes) || 0,
      }
    : null;
  const serviceContracts = Array.isArray(record.serviceContracts)
    ? record.serviceContracts
        .map((entry): ProjectForecastServiceContract | null => {
          if (!entry || typeof entry !== 'object') return null;
          const contract = entry as Record<string, unknown>;
          const id = safeText(contract.id);
          if (!id) return null;
          return {
            id,
            serviceCode: safeText(contract.serviceCode) || null,
            serviceName: safeText(contract.serviceName) || null,
            primaryTeamLabels: Array.isArray(contract.primaryTeamLabels)
              ? contract.primaryTeamLabels.map((value) => safeText(value)).filter(Boolean)
              : [],
            contractedMonthlyMinutesMin: parseOptionalNumber(contract.contractedMonthlyMinutesMin),
            contractedMonthlyMinutesMax: parseOptionalNumber(contract.contractedMonthlyMinutesMax),
            remainingBillableMinutesMin: parseOptionalNumber(contract.remainingBillableMinutesMin),
            remainingBillableMinutesMax: parseOptionalNumber(contract.remainingBillableMinutesMax),
            plannedRemainingEstimateMinutes: Number(contract.plannedRemainingEstimateMinutes) || 0,
          };
        })
        .filter((entry): entry is ProjectForecastServiceContract => Boolean(entry))
    : [];
  return {
    accountHealth,
    serviceContracts,
  };
}

function buildSubcategoryEstimateKey(
  serviceId: string | null | undefined,
  offeringCode: string | null | undefined,
  subcategoryId: string | null | undefined,
) {
  const normalizedServiceId = safeText(serviceId);
  const normalizedOfferingCode = safeText(offeringCode).toUpperCase();
  const normalizedSubcategoryId = safeText(subcategoryId);
  if (!normalizedServiceId || !normalizedOfferingCode || !normalizedSubcategoryId) return null;
  return `${normalizedServiceId}:${normalizedOfferingCode}:${normalizedSubcategoryId}`;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const ALL_TEAMS_VALUE = 'all_teams';
const ALL_ASSIGNEES_VALUE = 'all_assignees';
const ALL_PROJECTS_MOBILE_VALUE = 'all_projects_mobile';

function collectTicketTeamNodeIds(
  ticket:
    | Pick<UiTicket, 'teamId' | 'owningTeamNodeId' | 'currentTeamNodeId' | 'defaultTeamNodeId'>
    | Pick<Ticket, 'teamId' | 'owningTeamNodeId' | 'currentTeamNodeId' | 'defaultTeamNodeId'>,
): string[] {
  const values = [
    String(ticket.owningTeamNodeId ?? '').trim(),
    String(ticket.currentTeamNodeId ?? '').trim(),
    String(ticket.teamId ?? '').trim(),
    String(ticket.defaultTeamNodeId ?? '').trim(),
  ].filter(Boolean);

  return Array.from(new Set(values));
}

function ticketBelongsToTeam(
  ticket:
    | Pick<UiTicket, 'teamId' | 'owningTeamNodeId' | 'currentTeamNodeId' | 'defaultTeamNodeId'>
    | Pick<Ticket, 'teamId' | 'owningTeamNodeId' | 'currentTeamNodeId' | 'defaultTeamNodeId'>,
  teamNodeId: string,
): boolean {
  const normalizedTeamNodeId = String(teamNodeId ?? '').trim();
  if (!normalizedTeamNodeId || normalizedTeamNodeId === ALL_TEAMS_VALUE) return true;
  return collectTicketTeamNodeIds(ticket).includes(normalizedTeamNodeId);
}

export default function TeamsProjectsPage() {
  const dispatch = useDispatch<AppDispatch>();
  const api = useApi();
  const navigate = useNavigate();
  const viewerTimezone = React.useMemo(() => getUserTimezone() || 'UTC', []);
  const { orgId, organizations, role, user, isPaid, businessProfileComplete } = useAuth();
  const subscription = useSelector((state: RootState) => state.billing.subscription);
  const isClientWorkspace = role === 'client';
  const subscriptionStatus = (subscription as any)?.status as string | undefined;
  const isSubActive = React.useMemo(
    () => Boolean(isPaid || (subscriptionStatus && ['active', 'trialing'].includes(subscriptionStatus))),
    [isPaid, subscriptionStatus],
  );
  const clientWorkspaceReady = !isClientWorkspace || (isSubActive && businessProfileComplete);
  const clientCanCreateWork = !isClientWorkspace || clientWorkspaceReady;
  const clientWorkspaceOrganizations = React.useMemo(
    () => organizations.filter((organization) => organization.organizationType === 'business'),
    [organizations],
  );
  const [teamAssignments, setTeamAssignments] = React.useState<TeamAssignmentRow[]>([]);
  const [assigneeDirectory, setAssigneeDirectory] = React.useState<
    Record<string, { name: string; email: string }>
  >({});
  const [catalogServices, setCatalogServices] = React.useState<CatalogServiceEstimateRecord[]>([]);
  const [forecastOrgDetailsByOrgId, setForecastOrgDetailsByOrgId] = React.useState<
    Record<string, ProjectForecastOrgDetails | null | undefined>
  >({});
  const [teamCapacitySummaryByNodeId, setTeamCapacitySummaryByNodeId] = React.useState<
    Record<string, TeamCapacitySummary | null | undefined>
  >({});

  const all = useSelector(selectAllTickets);
  const ticketsLoading = useSelector(selectListLoading);
  const projects = useSelector(selectAllProjects);
  const { data: clientUsers = [] } = useUsers({
    q: '',
    role: 'client',
    page: 0,
    enabled: !isClientWorkspace,
  });

  React.useEffect(() => {
    if (!isClientWorkspace) return;
    void dispatch(getSubscription());
  }, [dispatch, isClientWorkspace]);

  React.useEffect(() => {
    if (isClientWorkspace && !clientWorkspaceReady) return;
    dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 }));
    dispatch(fetchProjects());
  }, [clientWorkspaceReady, dispatch, isClientWorkspace]);

  React.useEffect(() => {
    let mounted = true;
    if (isClientWorkspace && !clientWorkspaceReady) {
      setTeamAssignments([]);
      return () => {
        mounted = false;
      };
    }
    if (isClientWorkspace) {
      setTeamAssignments([]);
      return () => {
        mounted = false;
      };
    }
    (async () => {
      try {
        const response = await api.get('/projects/team-assignments');
        const next = Array.isArray(response?.data?.assignments)
          ? (response.data.assignments as TeamAssignmentRow[])
          : [];
        if (mounted) setTeamAssignments(next);
      } catch {
        if (mounted) setTeamAssignments([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [api, clientWorkspaceReady, isClientWorkspace]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await api.get('/tickets/assignees');
        const rows = Array.isArray(response?.data?.assignees)
          ? (response.data.assignees as Array<{ uid?: string; name?: string; email?: string }>)
          : [];
        if (!mounted) return;
        const next: Record<string, { name: string; email: string }> = {};
        for (const row of rows) {
          const uid = String(row?.uid ?? '').trim();
          if (!uid) continue;
          const name = String(row?.name ?? '').trim() || uid;
          const email = String(row?.email ?? '').trim();
          next[uid] = { name, email };
        }
        setAssigneeDirectory(next);
      } catch {
        if (mounted) setAssigneeDirectory({});
      }
    })();
    return () => {
      mounted = false;
    };
  }, [api]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await api.get('/catalog/services');
        const next = Array.isArray(response?.data?.services)
          ? (response.data.services as CatalogServiceEstimateRecord[])
          : [];
        if (mounted) setCatalogServices(next);
      } catch {
        if (mounted) setCatalogServices([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [api]);

  const uiTickets: UiTicket[] = React.useMemo(
    () =>
      all.map((t) => ({
        id: t.id,
        orgId: t.orgId ?? (t as any).orgId ?? null,
        ticketNumber: (t as any).ticketNumber ?? null,
        title: t.title,
        projectId: t.projectId || 'unassigned',
        milestoneId: (t as any).milestoneId ?? null,
        milestoneTitle: (t as any).milestoneTitle ?? null,
        clientId: t.clientId || 'unknown',
        clientBusinessName: t.clientBusinessName ?? null,
        clientFirstName: t.clientFirstName ?? null,
        clientLastName: t.clientLastName ?? null,
        clientDisplayName: t.clientDisplayName ?? null,
        clientEmail: t.clientEmail ?? null,
        createdBy: (t as any).createdBy ?? null,
        createdByFirstName: (t as any).createdByFirstName ?? null,
        createdByLastName: (t as any).createdByLastName ?? null,
        createdByDisplayName: (t as any).createdByDisplayName ?? null,
        createdByEmail: (t as any).createdByEmail ?? null,
        status: mapStatusToUi(String(t.status)),
        priority: mapPriorityToUi(String(t.priority)),
        timelineSortOrder:
          Number.isFinite(Number((t as any).timelineSortOrder))
            ? Math.max(0, Math.floor(Number((t as any).timelineSortOrder)))
            : 0,
        teamId: t.teamId ?? null,
        owningTeamNodeId: t.owningTeamNodeId ?? null,
        currentTeamNodeId: t.currentTeamNodeId ?? null,
        defaultTeamNodeId: t.defaultTeamNodeId ?? null,
        currentTeamLabel: t.currentTeamLabel ?? null,
        defaultTeamLabel: t.defaultTeamLabel ?? null,
        assignedAgentId: t.assignedAgentId || undefined,
        assignedAgentDisplayName: t.assignedAgentDisplayName ?? null,
        assignedAgentFirstName: t.assignedAgentFirstName ?? null,
        assignedAgentLastName: t.assignedAgentLastName ?? null,
        assignedAgentEmail: t.assignedAgentEmail ?? null,
        offeringCode: t.offeringCode ?? null,
        offeringName: t.offeringName ?? null,
        offeringSubcategoryId: t.offeringSubcategoryId ?? null,
        offeringSubcategoryName: t.offeringSubcategoryName ?? null,
        startAt: tsToIso((t as any).plannedStartAt ?? (t as any).slaResponseDueAt ?? t.createdAt ?? t.updatedAt ?? null),
        endAt: tsToIso((t as any).plannedEndAt ?? (t as any).slaResolutionDueAt ?? (t as any).plannedStartAt ?? (t as any).slaResponseDueAt ?? t.updatedAt ?? t.createdAt ?? null),
        updatedAt: tsToIso(t.updatedAt ?? t.createdAt ?? null),
      })),
    [all],
  );

  const subcategoryEstimateMinutesByKey = React.useMemo(() => {
    const next = new Map<string, number>();
    for (const service of catalogServices) {
      const serviceId = safeText(service?.id);
      if (!serviceId) continue;
      const metadata = service?.metadata && typeof service.metadata === 'object'
        ? service.metadata
        : {};
      const offerings = normalizeServiceOfferingCatalog(
        (metadata as Record<string, unknown>).offeringsCatalog,
      );
      for (const offering of offerings) {
        const offeringCode = safeText(offering.code).toUpperCase();
        if (!offeringCode) continue;
        for (const subcategory of offering.subcategories) {
          const estimateMinutes = normalizeNonNegativeInt(subcategory.estimatedMinutes);
          const key = buildSubcategoryEstimateKey(serviceId, offeringCode, subcategory.id);
          if (!key || estimateMinutes === null) continue;
          next.set(key, estimateMinutes);
        }
      }
    }
    return next;
  }, [catalogServices]);

  const resolvedTicketEstimateMinutesById = React.useMemo(() => {
    const next = new Map<string, number | null>();
    for (const ticket of all) {
      const explicitEstimateMinutes =
        normalizeNonNegativeInt(ticket.estimatedMinutes) ??
        normalizeNonNegativeInt(ticket.remainingEstimateMinutes);
      const subcategoryEstimateKey = buildSubcategoryEstimateKey(
        safeText(ticket.serviceId),
        safeText(ticket.offeringCode),
        safeText(ticket.offeringSubcategoryId),
      );
      const subcategoryEstimateMinutes = subcategoryEstimateKey
        ? subcategoryEstimateMinutesByKey.get(subcategoryEstimateKey) ?? null
        : null;
      const resolvedEstimateMinutes =
        subcategoryEstimateMinutes ??
        explicitEstimateMinutes;
      next.set(ticket.id, resolvedEstimateMinutes);
    }
    return next;
  }, [all, subcategoryEstimateMinutesByKey]);

  const effectiveTickets = React.useMemo(
    () =>
      all.map((ticket) => {
        const resolvedEstimateMinutes = resolvedTicketEstimateMinutesById.get(ticket.id) ?? null;
        return resolvedEstimateMinutes !== null && resolvedEstimateMinutes !== ticket.estimatedMinutes
          ? ({
              ...ticket,
              estimatedMinutes: resolvedEstimateMinutes,
            } as Ticket)
          : ticket;
      }),
    [all, resolvedTicketEstimateMinutesById],
  );

  const projectTicketsById = React.useMemo(() => {
    const next = new Map<string, Ticket[]>();
    for (const ticket of effectiveTickets) {
      const projectId = safeText(ticket.projectId);
      if (!projectId) continue;
      const existing = next.get(projectId);
      if (existing) {
        existing.push(ticket);
      } else {
        next.set(projectId, [ticket]);
      }
    }
    return next;
  }, [effectiveTickets]);

  const clientNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const user of clientUsers) {
      const label =
        String((user as any).workspaceDisplayName ?? '').trim() ||
        resolveClientDisplayName({
          businessName: (user as any).businessName,
          firstName: (user as any).firstName,
          lastName: (user as any).lastName,
          displayName: user.name,
          email: user.email,
          uid: user.id,
        });
      map.set(user.id, label);
    }
    for (const ticket of uiTickets) {
      const id = String(ticket.clientId ?? '').trim();
      if (!id || id === 'unknown') continue;
      if (map.has(id)) continue;
      const label = resolveClientDisplayName(
        {
          businessName: ticket.clientBusinessName,
          firstName: ticket.clientFirstName,
          lastName: ticket.clientLastName,
          displayName: ticket.clientDisplayName,
          email: ticket.clientEmail,
          uid: id,
        },
        '',
      );
      if (label) map.set(id, label);
    }
    return map;
  }, [clientUsers, uiTickets]);

  const clientAvatarById = React.useMemo(() => {
    const map = new Map<string, string | null>();
    for (const user of clientUsers) {
      map.set(
        user.id,
        String((user as any).workspaceAvatarURL ?? '').trim() || null,
      );
    }
    return map;
  }, [clientUsers]);

  const clientWorkspaceIdByUserId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const user of clientUsers) {
      const workspaceId = String((user as any).workspaceOrgId ?? '').trim();
      if (!workspaceId) continue;
      map.set(user.id, workspaceId);
    }
    return map;
  }, [clientUsers]);

  const workspaceRepresentativeClientIdByWorkspace = React.useMemo(() => {
    const grouped = new Map<
      string,
      {
        representativeClientId: string;
        isOwner: boolean;
      }
    >();

    for (const user of clientUsers) {
      const workspaceId = String((user as any).workspaceOrgId ?? '').trim();
      if (!workspaceId) continue;
      const isOwner = (user as any).workspaceRole === 'owner';
      const existing = grouped.get(workspaceId);
      if (!existing || (!existing.isOwner && isOwner)) {
        grouped.set(workspaceId, {
          representativeClientId: user.id,
          isOwner,
        });
      }
    }

    const map = new Map<string, string>();
    for (const [workspaceId, value] of grouped.entries()) {
      map.set(workspaceId, value.representativeClientId);
    }
    return map;
  }, [clientUsers]);

  const clientUserById = React.useMemo(() => {
    const map = new Map<string, (typeof clientUsers)[number]>();
    for (const userRow of clientUsers) {
      map.set(userRow.id, userRow);
    }
    return map;
  }, [clientUsers]);

  const teamOptions = React.useMemo<ReadonlyArray<GlassPicklistOption<string>>>(() => {
    const summary = new Map<
      string,
      { label: string; designationType: string; clients: Set<string> }
    >();
    for (const assignment of teamAssignments) {
      if (!assignment.teamNodeId) continue;
      const current = summary.get(assignment.teamNodeId) ?? {
        label: assignment.teamLabel || assignment.designationType || assignment.teamNodeId,
        designationType: assignment.designationType || '',
        clients: new Set<string>(),
      };
      current.clients.add(assignment.clientUid);
      if (!current.label && assignment.teamLabel) {
        current.label = assignment.teamLabel;
      }
      if (!current.designationType && assignment.designationType) {
        current.designationType = assignment.designationType;
      }
      summary.set(assignment.teamNodeId, current);
    }

    const teams = Array.from(summary.entries())
      .map(([teamNodeId, value]) => ({
        teamNodeId,
        label: value.label,
        designationType: value.designationType,
        clientCount: value.clients.size,
      }))
      .sort(
        (a, b) =>
          b.clientCount - a.clientCount ||
          a.label.localeCompare(b.label) ||
          a.teamNodeId.localeCompare(b.teamNodeId),
      );

    return [
      { value: ALL_TEAMS_VALUE, label: 'All teams' },
      ...teams.map((team) => ({
        value: team.teamNodeId,
        label: team.designationType && team.designationType !== team.label
          ? `${team.label} (${team.designationType})`
          : team.label,
      })),
    ];
  }, [teamAssignments]);

  const [selectedClient, setSelectedClient] = React.useState<string | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState<string | null>(
    isClientWorkspace
      ? (clientWorkspaceOrganizations.some((organization) => organization.orgId === orgId)
          ? orgId
          : clientWorkspaceOrganizations[0]?.orgId) ?? null
      : null,
  );
  const [selectedProject, setSelectedProject] = React.useState<string | null>(null);
  const [selectedMilestoneId, setSelectedMilestoneId] = React.useState<string | null>(null);
  const [expandedProjectId, setExpandedProjectId] = React.useState<string | null>(null);
  const [projectDetailsProjectId, setProjectDetailsProjectId] = React.useState<string | null>(null);
  const [openMilestoneMenu, setOpenMilestoneMenu] = React.useState<OpenMilestoneMenuState | null>(null);
  const [deleteMilestoneDialog, setDeleteMilestoneDialog] = React.useState<DeleteMilestoneDialogState | null>(null);
  const [deleteMilestoneHandling, setDeleteMilestoneHandling] = React.useState<'delete_tickets' | 'transfer_to_project'>('transfer_to_project');
  const [deleteMilestoneConfirmWord, setDeleteMilestoneConfirmWord] = React.useState('');
  const [deletingMilestone, setDeletingMilestone] = React.useState(false);
  const [deleteProjectDialog, setDeleteProjectDialog] = React.useState<DeleteProjectDialogState | null>(null);
  const [deleteProjectHandling, setDeleteProjectHandling] = React.useState<'delete_tickets' | 'keep_tickets'>('keep_tickets');
  const [deleteProjectConfirmWord, setDeleteProjectConfirmWord] = React.useState('');
  const [deletingProject, setDeletingProject] = React.useState(false);
  const [duplicateProjectDialog, setDuplicateProjectDialog] = React.useState<DuplicateProjectDialogState | null>(null);
  const [selectedTeamId, setSelectedTeamId] = React.useState<string>(ALL_TEAMS_VALUE);
  const [selectedAssigneeId, setSelectedAssigneeId] = React.useState<string>(ALL_ASSIGNEES_VALUE);
  const [isMobileProjectsLayout, setIsMobileProjectsLayout] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 899px)').matches;
  });
  const [timelineManualOrderByProject, setTimelineManualOrderByProject] = React.useState<Record<string, string[]>>({});
  const [projectMilestoneComposerByProject, setProjectMilestoneComposerByProject] = React.useState<Record<string, ProjectMilestoneComposer>>({});
  const milestoneDateInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(max-width: 899px)');
    const sync = () => setIsMobileProjectsLayout(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  React.useEffect(() => {
    if (!isClientWorkspace) {
      if (selectedOrganizationId !== null) {
        setSelectedOrganizationId(null);
      }
      return;
    }
    const availableIds = clientWorkspaceOrganizations.map((organization) => organization.orgId);
    const preferredOrgId = orgId ?? availableIds[0] ?? null;
    const resolvedPreferredOrgId = availableIds.includes(String(preferredOrgId ?? ''))
      ? preferredOrgId
      : availableIds[0] ?? null;
    if (!selectedOrganizationId) {
      if (resolvedPreferredOrgId) setSelectedOrganizationId(resolvedPreferredOrgId);
      return;
    }
    if (availableIds.includes(selectedOrganizationId)) return;
    setSelectedOrganizationId(resolvedPreferredOrgId);
  }, [clientWorkspaceOrganizations, isClientWorkspace, orgId, selectedOrganizationId]);

  React.useEffect(() => {
    setSelectedProject(null);
    setSelectedMilestoneId(null);
    setExpandedProjectId(null);
    setOpenMilestoneMenu(null);
  }, [selectedClient, selectedOrganizationId, selectedTeamId]);

  React.useEffect(() => {
    if (!isClientWorkspace || !isMobileProjectsLayout) return;
    if (selectedMilestoneId) setSelectedMilestoneId(null);
    if (expandedProjectId) setExpandedProjectId(null);
    if (projectDetailsProjectId) setProjectDetailsProjectId(null);
  }, [
    expandedProjectId,
    isClientWorkspace,
    isMobileProjectsLayout,
    projectDetailsProjectId,
    selectedMilestoneId,
  ]);

  React.useEffect(() => {
    if (!openMilestoneMenu) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-milestone-menu-root="true"]')) return;
      setOpenMilestoneMenu(null);
    };

    const handleWindowChange = () => {
      setOpenMilestoneMenu(null);
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [openMilestoneMenu]);

  const selectedTeamWorkspaceIds = React.useMemo(() => {
    if (selectedTeamId === ALL_TEAMS_VALUE) return null;
    const ids = new Set<string>();
    for (const assignment of teamAssignments) {
      if (assignment.teamNodeId !== selectedTeamId) continue;
      if (!assignment.clientUid) continue;
      const workspaceId = clientWorkspaceIdByUserId.get(assignment.clientUid);
      if (!workspaceId) continue;
      ids.add(workspaceId);
    }
    for (const ticket of uiTickets) {
      if (!ticketBelongsToTeam(ticket, selectedTeamId)) continue;
      const workspaceId =
        String(ticket.orgId ?? '').trim() ||
        clientWorkspaceIdByUserId.get(String(ticket.clientId ?? '').trim()) ||
        '';
      if (!workspaceId) continue;
      ids.add(workspaceId);
    }
    return ids;
  }, [clientWorkspaceIdByUserId, selectedTeamId, teamAssignments, uiTickets]);

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [clientEditTicketId, setClientEditTicketId] = React.useState<string | null>(null);
  const [createSeedRange, setCreateSeedRange] = React.useState<{ startAt: string; endAt: string } | null>(null);
  const [createProjectOpen, setCreateProjectOpen] = React.useState(false);
  const clientEditingTicket = useSelector((state: RootState) =>
    clientEditTicketId ? selectCachedTicketById(clientEditTicketId)(state) : undefined,
  );

  const openCreate = React.useCallback((seedDateIso?: unknown) => {
    if (!clientCanCreateWork) return;
    const seedIso = typeof seedDateIso === 'string' ? seedDateIso : undefined;
    if (seedIso) {
      const startAt = new Date(seedIso);
      startAt.setHours(9, 0, 0, 0);
      const endAt = new Date(startAt.getTime() + DAY_MS);
      setCreateSeedRange({
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
      });
    } else {
      setCreateSeedRange(null);
    }
    setEditorOpen(true);
  }, [clientCanCreateWork]);

  const openEdit = React.useCallback((id: string) => {
    if (isClientWorkspace) {
      setClientEditTicketId(id);
      return;
    }
    dispatch(openTicketPortal({ ticketId: id }));
  }, [dispatch, isClientWorkspace]);

  const handleProjectSelect = React.useCallback((projectId: string | null) => {
    setSelectedProject(projectId);
    setSelectedMilestoneId(null);
  }, []);

  const handleMilestoneSelect = React.useCallback((projectId: string, milestoneId: string) => {
    setSelectedProject(projectId);
    setSelectedMilestoneId(milestoneId);
  }, []);

  const openCreateProject = React.useCallback(() => {
    if (!clientCanCreateWork) return;
    if (isClientWorkspace) {
      if (!selectedOrganizationId) return;
    } else if (!selectedClient) {
      return;
    }
    setCreateProjectOpen(true);
  }, [clientCanCreateWork, isClientWorkspace, selectedClient, selectedOrganizationId]);

  const handleCreateProject = React.useCallback(async (payload: { name: string; color: string; code?: string }) => {
    if (isClientWorkspace) {
      if (!selectedOrganizationId) return;
    } else if (!selectedClient) {
      return;
    }
    setCreateProjectOpen(false);
    const representativeClientId = !isClientWorkspace && selectedClient
      ? workspaceRepresentativeClientIdByWorkspace.get(selectedClient) ?? null
      : null;

    const createPayload = {
      ...payload,
      timezone: viewerTimezone,
      ...(isClientWorkspace
        ? { orgId: selectedOrganizationId ?? orgId ?? undefined }
        : {
            clientId: representativeClientId ?? undefined,
            orgId: String(selectedClient ?? '').trim() || undefined,
          }),
      ...(selectedTeamId !== ALL_TEAMS_VALUE ? { teamNodeId: selectedTeamId } : {}),
    };
    const result = await dispatch(
      createProject(createPayload),
    ).unwrap();
    await dispatch(fetchProjects());
    handleProjectSelect(result.id);
  }, [dispatch, handleProjectSelect, isClientWorkspace, orgId, selectedClient, selectedOrganizationId, selectedTeamId, viewerTimezone, workspaceRepresentativeClientIdByWorkspace]);

  const canDeleteProjects = role === 'admin' || role === 'agent';

  const handleDeleteProject = React.useCallback(
    async (project: TeamFilterItem) => {
      if (!canDeleteProjects) return;
      if (!project.id) return;

      const childTicketCount = uiTickets.filter((ticket) => ticket.projectId === project.id).length;
      setDeleteProjectHandling(childTicketCount > 0 ? 'keep_tickets' : 'delete_tickets');
      setDeleteProjectConfirmWord('');
      setDeleteProjectDialog({
        projectId: project.id,
        projectTitle: project.label,
        childTicketCount,
      });
    },
    [canDeleteProjects, uiTickets],
  );

  const handleDuplicateProject = React.useCallback(
    async (payload: { projectId: string; name: string }) => {
      const result = await dispatch(duplicateProject(payload)).unwrap();
      await Promise.all([
        dispatch(fetchProjects()),
        dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 })),
      ]);
      setDuplicateProjectDialog(null);
      handleProjectSelect(result.id);
      setExpandedProjectId(result.id);
    },
    [dispatch, handleProjectSelect],
  );

  const openDeleteMilestoneDialog = React.useCallback((
    projectId: string,
    milestoneId: string,
    milestoneTitle: string,
    childTicketCount: number,
  ) => {
    setOpenMilestoneMenu(null);
    setDeleteMilestoneHandling(childTicketCount > 0 ? 'transfer_to_project' : 'delete_tickets');
    setDeleteMilestoneConfirmWord('');
    setDeleteMilestoneDialog({
      projectId,
      milestoneId,
      milestoneTitle,
      childTicketCount,
    });
  }, []);

  const closeDeleteMilestoneDialog = React.useCallback(() => {
    if (deletingMilestone) return;
    setDeleteMilestoneDialog(null);
    setDeleteMilestoneConfirmWord('');
  }, [deletingMilestone]);

  const closeDeleteProjectDialog = React.useCallback(() => {
    if (deletingProject) return;
    setDeleteProjectDialog(null);
    setDeleteProjectConfirmWord('');
  }, [deletingProject]);

  const handleConfirmDeleteMilestone = React.useCallback(async () => {
    if (!deleteMilestoneDialog || deletingMilestone) return;
    setDeletingMilestone(true);
    try {
      await dispatch(deleteProjectMilestone({
        projectId: deleteMilestoneDialog.projectId,
        milestoneId: deleteMilestoneDialog.milestoneId,
        ticketHandling: deleteMilestoneHandling,
      })).unwrap();
      if (selectedMilestoneId === deleteMilestoneDialog.milestoneId) {
        setSelectedMilestoneId(null);
      }
      setDeleteMilestoneDialog(null);
      setDeleteMilestoneConfirmWord('');
      await Promise.all([
        dispatch(fetchProjects()),
        dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 })),
      ]);
    } catch (error) {
      console.error('[TeamsProjectsPage] Failed to delete milestone', error);
    } finally {
      setDeletingMilestone(false);
    }
  }, [
    deleteMilestoneDialog,
    deleteMilestoneHandling,
    deletingMilestone,
    dispatch,
    selectedMilestoneId,
  ]);

  const handleConfirmDeleteProject = React.useCallback(async () => {
    if (!deleteProjectDialog || deletingProject) return;
    setDeletingProject(true);
    try {
      await dispatch(deleteProject({
        projectId: deleteProjectDialog.projectId,
        ticketHandling: deleteProjectHandling,
      })).unwrap();
      if (selectedProject === deleteProjectDialog.projectId) {
        handleProjectSelect(null);
      }
      if (expandedProjectId === deleteProjectDialog.projectId) {
        setExpandedProjectId(null);
      }
      if (projectDetailsProjectId === deleteProjectDialog.projectId) {
        setProjectDetailsProjectId(null);
      }
      setSelectedMilestoneId(null);
      setDeleteProjectDialog(null);
      setDeleteProjectConfirmWord('');
      await Promise.all([
        dispatch(fetchProjects()),
        dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 })),
      ]);
    } catch (error) {
      console.error('[TeamsProjectsPage] Failed to delete project', error);
    } finally {
      setDeletingProject(false);
    }
  }, [
    deleteProjectDialog,
    deleteProjectHandling,
    deletingProject,
    dispatch,
    expandedProjectId,
    handleProjectSelect,
    projectDetailsProjectId,
    selectedProject,
  ]);

  async function handleCreate(payload: {
    title: string;
    description: string;
    projectId: string;
    milestoneId?: string | null;
    priority: UiPriority;
    assignedAgentId: string | null;
    assignedOrgMemberId?: string | null;
    owningTeamNodeId?: string | null;
    clientSubscriptionId?: string | null;
    offeringId?: string | null;
    offeringSubcategoryId?: string | null;
    kbaseProcessId?: string | null;
    estimatedMinutes?: number | null;
    blockedByTicketIds?: string[];
    handoffToTicketIds?: string[];
    subtasks: any[];
  }): Promise<string> {
    const apiPriority = mapPriorityToApi(payload.priority as UiPriority);
    const selectedProjectIdForCreate = selectedProject ?? null;
    // Always prefer the project currently selected in the Teams/Projects UI.
    // This guarantees new tickets land in the active project lane even if the drawer project field
    // was left untouched by the user.
    const effectiveProjectId = payload.projectId || selectedProjectIdForCreate || null;
    const selectedProjectRecord = effectiveProjectId
      ? (projectById(effectiveProjectId) as any)
      : null;
    const selectedProjectOrgId = String(selectedProjectRecord?.orgId ?? '').trim() || null;

    // Client context should follow the selected project owner when available.
    const effectiveClientId = isClientWorkspace
      ? selectedProjectRecord?.clientId || selectedProjectRecord?.ownerUid || user?.uid || null
      : workspaceRepresentativeClientIdByWorkspace.get(selectedClient ?? '') ||
        selectedProjectRecord?.clientId ||
        selectedProjectRecord?.ownerUid ||
        null;
    const orgIdForCreate: string | undefined =
      selectedProjectOrgId ||
      (isClientWorkspace ? selectedOrganizationId ?? orgId ?? undefined : selectedClient ?? orgId ?? undefined);
    const effectiveOwningTeamNodeId =
      payload.owningTeamNodeId ??
      (selectedTeamId !== ALL_TEAMS_VALUE ? selectedTeamId : null) ??
      null;

    const res = await dispatch(
      createTicketThunk({
        title: payload.title,
        description: payload.description,
        priority: apiPriority,
        projectId: effectiveProjectId,
        milestoneId: payload.milestoneId ?? selectedMilestoneId ?? null,
        clientId: effectiveClientId,
        clientSubscriptionId: payload.clientSubscriptionId ?? null,
        offeringId: payload.offeringId ?? null,
        offeringSubcategoryId: payload.offeringSubcategoryId ?? null,
        kbaseProcessId: payload.kbaseProcessId ?? null,
        assignedOrgMemberId: payload.assignedOrgMemberId ?? null,
        owningTeamNodeId: effectiveOwningTeamNodeId,
        estimatedMinutes: payload.estimatedMinutes ?? null,
        blockedByTicketIds: payload.blockedByTicketIds ?? [],
        handoffToTicketIds: payload.handoffToTicketIds ?? [],
        externalLinks: [],
        tags: [],
        teamId: effectiveOwningTeamNodeId ?? selectedProjectRecord?.teamNodeId ?? null,
        slaPolicyId: null,
        orgId: orgIdForCreate,
        plannedStartAt: createSeedRange?.startAt ?? null,
        plannedEndAt: createSeedRange?.endAt ?? null,
        timezone: viewerTimezone,
      }),
    ).unwrap();

    const postCreatePatch: any = {};
    if (payload.subtasks?.length) postCreatePatch.subtasks = payload.subtasks;
    if (payload.assignedAgentId) postCreatePatch.assignedAgentId = payload.assignedAgentId;
    if (payload.assignedOrgMemberId) {
      postCreatePatch.assignedOrgMemberId = payload.assignedOrgMemberId;
    }
    if (Object.keys(postCreatePatch).length) {
      await handleUpdate(res.id, postCreatePatch);
    }

    setCreateSeedRange(null);
    await dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 }));
    return res.id;
  }

  async function handleUpdate(id: string, patch: Partial<{
    title: string;
    description: string;
    projectId: string;
    milestoneId: string | null;
    priority: UiPriority;
    startDate: string;
    dueDate: string;
    assignedAgentId: string | null;
    assignedOrgMemberId: string | null;
    owningTeamNodeId?: string | null;
    clientSubscriptionId: string | null;
    offeringId: string | null;
    offeringSubcategoryId: string | null;
    kbaseProcessId: string | null;
    estimatedMinutes: number | null;
    blockedByTicketIds: string[];
    handoffToTicketIds: string[];
    status: 'open' | 'in_progress' | 'blocked' | 'resolved' | 'closed';
    subtasks: any[];
    participants: string[];
  }>) {
    if (patch.assignedAgentId !== undefined) {
      await dispatch(assignTicket({ id, agentId: patch.assignedAgentId })).unwrap();
    }
    if (patch.status) {
      await dispatch(updateTicketStatus({ id, status: patch.status })).unwrap();
    }

    const body: any = {};
    if (patch.title !== undefined) body.title = patch.title;
    if (patch.description !== undefined) body.description = patch.description;
    if (patch.projectId !== undefined) body.projectId = patch.projectId || null;
    if (patch.milestoneId !== undefined) body.milestoneId = patch.milestoneId || null;
    if (patch.priority !== undefined) body.priority = mapPriorityToApi(patch.priority);
    if (patch.clientSubscriptionId !== undefined) {
      body.clientSubscriptionId = patch.clientSubscriptionId || null;
    }
    if (patch.offeringId !== undefined) body.offeringId = patch.offeringId || null;
    if (patch.offeringSubcategoryId !== undefined) {
      body.offeringSubcategoryId = patch.offeringSubcategoryId || null;
    }
    if (patch.kbaseProcessId !== undefined) {
      body.kbaseProcessId = patch.kbaseProcessId || null;
    }
    if (patch.assignedOrgMemberId !== undefined) {
      body.assignedOrgMemberId = patch.assignedOrgMemberId || null;
    }
    if (patch.owningTeamNodeId !== undefined) {
      body.owningTeamNodeId = patch.owningTeamNodeId || null;
    }
    if (patch.estimatedMinutes !== undefined) {
      body.estimatedMinutes = patch.estimatedMinutes;
      body.remainingEstimateMinutes = patch.estimatedMinutes;
    }
    if (patch.blockedByTicketIds !== undefined) {
      body.blockedByTicketIds = patch.blockedByTicketIds;
    }
    if (patch.handoffToTicketIds !== undefined) {
      body.handoffToTicketIds = patch.handoffToTicketIds;
    }
    if (patch.startDate !== undefined) {
      body.startDate = patch.startDate || null;
      body.timezone = viewerTimezone;
    }
    if (patch.dueDate !== undefined) {
      body.dueDate = patch.dueDate || null;
      body.timezone = viewerTimezone;
    }
    if (patch.subtasks !== undefined) body.subtasks = patch.subtasks;
    if (patch.participants !== undefined) body.participants = patch.participants;

    if (Object.keys(body).length) {
      await api.patch(`/tickets/${id}`, body);
    }

    await dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 }));
  }

  const projectById = React.useCallback(
    (id?: string) => projects.find((project) => project.id === id) || null,
    [projects],
  );

  const clientEditingProject = React.useMemo(() => {
    if (!clientEditingTicket?.projectId) return undefined;
    return projects.find((project) => project.id === clientEditingTicket.projectId) || undefined;
  }, [clientEditingTicket?.projectId, projects]);

  const clientEditorTicket = React.useMemo(
    () => buildTicketEditorTicket(clientEditingTicket),
    [clientEditingTicket],
  );

  const projectNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const project of projects) {
      map.set(project.id, project.name);
    }
    return map;
  }, [projects]);

  const teamLabelByNodeId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const assignment of teamAssignments) {
      const teamNodeId = String(assignment.teamNodeId ?? '').trim();
      if (!teamNodeId) continue;
      if (map.has(teamNodeId)) continue;
      map.set(
        teamNodeId,
        assignment.teamLabel || assignment.designationType || teamNodeId,
      );
    }
    return map;
  }, [teamAssignments]);

  const isOpenLike = React.useCallback(
    (status: UiStatus) =>
      status === 'submitted' ||
      status === 'triage' ||
      status === 'in_progress' ||
      status === 'blocked',
    [],
  );

  const organizationLabelById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const organization of organizations) {
      const label = String(
        organization.displayLabel ||
        organization.businessName ||
        organization.orgName ||
        '',
      ).trim();
      if (!label) continue;
      map.set(organization.orgId, label);
    }
    for (const project of projects) {
      const projectOrgId = String((project as any).orgId ?? '').trim();
      if (!projectOrgId || map.has(projectOrgId)) continue;
      map.set(projectOrgId, projectOrgId);
    }
    for (const ticket of uiTickets) {
      const ticketOrgId = String(ticket.orgId ?? '').trim();
      if (!ticketOrgId || map.has(ticketOrgId)) continue;
      map.set(ticketOrgId, ticketOrgId);
    }
    return map;
  }, [organizations, projects, uiTickets]);

  const organizationAvatarById = React.useMemo(() => {
    const map = new Map<string, string | null>();
    for (const organization of organizations) {
      map.set(
        organization.orgId,
        String(organization.avatarURL || organization.logoURL || '').trim() || null,
      );
    }
    return map;
  }, [organizations]);

  const organizationItems = React.useMemo<TeamFilterItem[]>(() => {
    const countByOrganization = new Map<string, number>();
    for (const ticket of uiTickets) {
      if (!isOpenLike(ticket.status)) continue;
      const id = String(ticket.orgId ?? '').trim();
      if (!id) continue;
      countByOrganization.set(id, (countByOrganization.get(id) ?? 0) + 1);
    }

    const sourceOrganizations = isClientWorkspace
      ? clientWorkspaceOrganizations
      : organizations;

    const fromMemberships = sourceOrganizations.map((organization) => {
      const id = organization.orgId;
      return {
        id,
        label:
          organization.displayLabel ||
          organization.businessName ||
          organization.orgName ||
          organizationLabelById.get(id) ||
          id,
        openCount: countByOrganization.get(id) ?? 0,
        avatarKey: id,
        avatarUrl: organization.avatarURL || organization.logoURL || null,
      };
    });

    const seen = new Set(fromMemberships.map((item) => item.id));
    const fromTickets = isClientWorkspace
      ? []
      : Array.from(countByOrganization.entries())
      .filter(([id]) => !seen.has(id))
      .map(([id, openCount]) => ({
        id,
        label: organizationLabelById.get(id) || id,
        openCount,
        avatarKey: id,
        avatarUrl: organizationAvatarById.get(id) || null,
      }));

    return [...fromMemberships, ...fromTickets];
  }, [clientWorkspaceOrganizations, isClientWorkspace, isOpenLike, organizationAvatarById, organizationLabelById, organizations, uiTickets]);

  const clientItems = React.useMemo<TeamFilterItem[]>(() => {
    const countByWorkspace = new Map<string, number>();
    for (const ticket of uiTickets) {
      if (!isOpenLike(ticket.status)) continue;
      const id =
        String(ticket.orgId ?? '').trim() ||
        clientWorkspaceIdByUserId.get(String(ticket.clientId ?? '').trim()) ||
        '';
      if (!id) continue;
      countByWorkspace.set(id, (countByWorkspace.get(id) ?? 0) + 1);
    }

    const grouped = new Map<
      string,
      {
        id: string;
        label: string;
        openCount: number;
        avatarKey: string;
        avatarUrl: string | null;
      }
    >();

    for (const user of clientUsers) {
      const workspaceId = String((user as any).workspaceOrgId ?? '').trim() || user.id;
      if (!grouped.has(workspaceId)) {
        grouped.set(workspaceId, {
          id: workspaceId,
          label:
            String((user as any).workspaceDisplayName ?? '').trim() ||
            clientNameById.get(user.id) ||
            workspaceId,
          openCount: countByWorkspace.get(workspaceId) ?? 0,
          avatarKey: workspaceId,
          avatarUrl:
            String((user as any).workspaceAvatarURL ?? '').trim() ||
            clientAvatarById.get(user.id) ||
            null,
        });
      }
    }

    const fromCrm = Array.from(grouped.values());

    // Preserve visibility for ticketed clients that may not appear in the current CRM page window.
    const seen = new Set(fromCrm.map((item) => item.id));
    const fromTickets = Array.from(countByWorkspace.entries())
      .filter(([id]) => !seen.has(id))
      .map(([id, openCount]) => ({
        id,
        label: organizationLabelById.get(id) || id,
        openCount,
        avatarKey: id,
        avatarUrl: organizationAvatarById.get(id) || null,
      }));

    const allItems = [...fromCrm, ...fromTickets];
    if (!selectedTeamWorkspaceIds) return allItems;
    return allItems.filter((item) => selectedTeamWorkspaceIds.has(item.id));
  }, [
    clientAvatarById,
    clientNameById,
    clientUsers,
    clientWorkspaceIdByUserId,
    isOpenLike,
    organizationAvatarById,
    organizationLabelById,
    selectedTeamWorkspaceIds,
    uiTickets,
  ]);

  React.useEffect(() => {
    if (isClientWorkspace) return;
    if (!selectedClient) return;
    if (clientItems.some((item) => item.id === selectedClient)) return;
    setSelectedClient(null);
  }, [clientItems, isClientWorkspace, selectedClient]);

  const scopedByTeam = React.useMemo(() => {
    if (isClientWorkspace) return uiTickets;
    if (selectedTeamId === ALL_TEAMS_VALUE) return uiTickets;
    return uiTickets.filter((ticket) => ticketBelongsToTeam(ticket, selectedTeamId));
  }, [isClientWorkspace, selectedTeamId, uiTickets]);

  const scopedByOrganization = React.useMemo(() => {
    if (!isClientWorkspace) return scopedByTeam;
    if (!selectedOrganizationId) return [];
    return scopedByTeam.filter(
      (ticket) => String(ticket.orgId ?? '').trim() === selectedOrganizationId,
    );
  }, [isClientWorkspace, scopedByTeam, selectedOrganizationId]);

  const scopedByClient = React.useMemo(() => {
    if (isClientWorkspace) return scopedByOrganization;
    if (!selectedClient) return scopedByTeam;
    return scopedByTeam.filter(
      (ticket) =>
        (String(ticket.orgId ?? '').trim() ||
          clientWorkspaceIdByUserId.get(String(ticket.clientId ?? '').trim()) ||
          '') === selectedClient,
    );
  }, [clientWorkspaceIdByUserId, isClientWorkspace, scopedByOrganization, scopedByTeam, selectedClient]);

  React.useEffect(() => {
    if (!isClientWorkspace) return;
    const preferredOrganizationId = organizationItems.some((item) => item.id === orgId)
      ? orgId
      : organizationItems[0]?.id ?? null;
    if (!selectedOrganizationId) {
      if (organizationItems.length) {
        setSelectedOrganizationId(preferredOrganizationId);
      }
      return;
    }
    if (organizationItems.some((item) => item.id === selectedOrganizationId)) return;
    setSelectedOrganizationId(preferredOrganizationId);
  }, [isClientWorkspace, orgId, organizationItems, selectedOrganizationId]);

  const visibleProjects = React.useMemo(() => {
    const visibleProjectIds = new Set<string>();
    for (const ticket of scopedByClient) {
      const id = String(ticket.projectId ?? '').trim();
      if (id) visibleProjectIds.add(id);
    }

    return projects.filter((project) => {
      if (isClientWorkspace) {
        if (!selectedOrganizationId) return false;
        const projectOrgId = String((project as any).orgId ?? '').trim();
        return projectOrgId === selectedOrganizationId;
      }

      const projectOrgId = String((project as any).orgId ?? '').trim();
      if (selectedClient && projectOrgId !== selectedClient) return false;

      if (selectedTeamId !== ALL_TEAMS_VALUE) {
        const projectTeamNodeId = String((project as any).teamNodeId ?? '').trim();
        if (projectTeamNodeId === selectedTeamId) return true;
        return visibleProjectIds.has(project.id);
      }

      return true;
    });
  }, [
    isClientWorkspace,
    projects,
    scopedByClient,
    selectedClient,
    selectedOrganizationId,
    selectedTeamId,
  ]);

  const projectItems = React.useMemo<TeamFilterItem[]>(() => {
    const sourceForCounts = scopedByClient;
    const countByProject = new Map<string, number>();
    for (const ticket of sourceForCounts) {
      const id = String(ticket.projectId ?? '').trim();
      if (!id) continue;
      if (!isOpenLike(ticket.status)) continue;
      countByProject.set(id, (countByProject.get(id) ?? 0) + 1);
    }

    const baseItems: TeamFilterItem[] = visibleProjects.map((project) => ({
      id: project.id,
      label: project.name,
      openCount: countByProject.get(project.id) ?? 0,
      avatarKey: project.id,
    }));

    // Backstop for legacy tickets referencing projects outside current fetched window.
    const seen = new Set(baseItems.map((item) => item.id));
    for (const [projectId, openCount] of countByProject.entries()) {
      if (seen.has(projectId)) continue;
      baseItems.push({
        id: projectId,
        label: projectNameById.get(projectId) || projectId,
        openCount,
        avatarKey: projectId,
      });
    }

    return baseItems;
  }, [isOpenLike, projectNameById, scopedByClient, visibleProjects]);

  React.useEffect(() => {
    if (!selectedProject) return;
    if (projectItems.some((item) => item.id === selectedProject)) return;
    setSelectedProject(null);
  }, [projectItems, selectedProject]);

  React.useEffect(() => {
    if (!expandedProjectId) return;
    if (projectItems.some((item) => item.id === expandedProjectId)) return;
    setExpandedProjectId(null);
  }, [expandedProjectId, projectItems]);

  React.useEffect(() => {
    if (!projectDetailsProjectId) return;
    if (projectItems.some((item) => item.id === projectDetailsProjectId)) return;
    setProjectDetailsProjectId(null);
  }, [projectDetailsProjectId, projectItems]);

  const mobileWorkspaceOptions = React.useMemo<ReadonlyArray<GlassPicklistOption<string>>>(() => {
    if (!isClientWorkspace) return [];
    return organizationItems.map((item) => ({
      value: item.id,
      label: item.label,
    }));
  }, [isClientWorkspace, organizationItems]);

  const mobileProjectOptions = React.useMemo<ReadonlyArray<GlassPicklistOption<string>>>(() => {
    return [
      { value: ALL_PROJECTS_MOBILE_VALUE, label: 'All projects' },
      ...projectItems.map((item) => ({
        value: item.id,
        label: item.label,
      })),
    ];
  }, [projectItems]);

  const selectedProjectRecord = React.useMemo(
    () => (selectedProject ? (projectById(selectedProject) as any) : null),
    [projectById, selectedProject],
  );

  const selectedMilestoneRecord = React.useMemo(() => {
    if (!selectedProjectRecord || !selectedMilestoneId) return null;
    const milestones = Array.isArray(selectedProjectRecord.milestones)
      ? selectedProjectRecord.milestones
      : [];
    return milestones.find((entry: any) => entry.id === selectedMilestoneId) ?? null;
  }, [selectedMilestoneId, selectedProjectRecord]);

  React.useEffect(() => {
    if (!selectedMilestoneId) return;
    if (!selectedProjectRecord || !selectedMilestoneRecord) {
      setSelectedMilestoneId(null);
    }
  }, [selectedMilestoneId, selectedMilestoneRecord, selectedProject, selectedProjectRecord]);

  const scopedTickets = React.useMemo(() => {
    if (!selectedProject) return scopedByClient;
    return scopedByClient.filter((ticket) => {
      if (ticket.projectId !== selectedProject) return false;
      if (!selectedMilestoneId) return true;
      return String(ticket.milestoneId ?? '').trim() === selectedMilestoneId;
    });
  }, [scopedByClient, selectedMilestoneId, selectedProject]);

  const ticketsTableRows = React.useMemo(
    () =>
      scopedTickets.map((ticket) => {
        const currentTeamNodeId =
          safeText(ticket.currentTeamNodeId) ||
          safeText(ticket.owningTeamNodeId) ||
          safeText(ticket.teamId) ||
          safeText(ticket.defaultTeamNodeId) ||
          null;
        const defaultTeamNodeId =
          safeText(ticket.defaultTeamNodeId) ||
          currentTeamNodeId ||
          null;
        const currentTeamLabel =
          safeText(ticket.currentTeamLabel) ||
          null;
        const defaultTeamLabel =
          safeText(ticket.defaultTeamLabel) ||
          null;

        if (
          currentTeamNodeId === (ticket.currentTeamNodeId ?? null) &&
          defaultTeamNodeId === (ticket.defaultTeamNodeId ?? null) &&
          currentTeamLabel === (ticket.currentTeamLabel ?? null) &&
          defaultTeamLabel === (ticket.defaultTeamLabel ?? null)
        ) {
          return ticket;
        }

        return {
          ...ticket,
          currentTeamNodeId,
          defaultTeamNodeId,
          currentTeamLabel,
          defaultTeamLabel,
        };
      }),
    [scopedTickets],
  );

  const timelineBaseTickets = React.useMemo(
    () => (selectedProject ? scopedTickets : scopedByClient),
    [scopedTickets, scopedByClient, selectedProject],
  );

  const timelineOrderKey = React.useMemo(
    () =>
      selectedMilestoneId
        ? `milestone:${selectedMilestoneId}`
        : (selectedProject ? `project:${selectedProject}` : null),
    [selectedMilestoneId, selectedProject],
  );

  const timelineDefaultOrderIds = React.useMemo(() => {
    return [...timelineBaseTickets]
      .sort((a, b) => {
        if (a.timelineSortOrder !== b.timelineSortOrder) {
          return a.timelineSortOrder - b.timelineSortOrder;
        }
        const updatedDelta = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        if (updatedDelta !== 0) return updatedDelta;
        return a.id.localeCompare(b.id);
      })
      .map((ticket) => ticket.id);
  }, [timelineBaseTickets]);

  React.useEffect(() => {
    if (!timelineOrderKey) return;
    setTimelineManualOrderByProject((previous) => {
      const current = previous[timelineOrderKey];
      if (!current || current.length === 0) return previous;

      const fallbackSet = new Set(timelineDefaultOrderIds);
      const reconciled = current.filter((id) => fallbackSet.has(id));
      const seen = new Set(reconciled);
      for (const id of timelineDefaultOrderIds) {
        if (seen.has(id)) continue;
        reconciled.push(id);
        seen.add(id);
      }

      if (arraysEqual(current, reconciled)) return previous;
      return {
        ...previous,
        [timelineOrderKey]: reconciled,
      };
    });
  }, [timelineDefaultOrderIds, timelineOrderKey]);

  const persistTimelineOrder = React.useCallback(async (orderedTicketIds: string[]) => {
    if (!selectedProject) return;
    try {
      await api.post('/tickets/reorder', {
        projectId: selectedProject,
        orderedTicketIds,
      });
    } catch (error) {
      console.error('[TeamsProjectsPage] Failed to persist timeline ticket order', error);
      if (timelineOrderKey) {
        setTimelineManualOrderByProject((previous) => {
          if (!previous[timelineOrderKey]) return previous;
          const next = { ...previous };
          delete next[timelineOrderKey];
          return next;
        });
      }
      await dispatch(fetchTickets({ orderBy: 'plannedEndAt', direction: 'asc', limit: 200 }));
    }
  }, [api, dispatch, selectedProject, timelineOrderKey]);

  const handleTimelineLaneOrderChange = React.useCallback((nextOrderedVisibleIds: string[]) => {
    if (!timelineOrderKey) return;

    const currentFullOrder = timelineManualOrderByProject[timelineOrderKey]?.length
      ? timelineManualOrderByProject[timelineOrderKey]
      : timelineDefaultOrderIds;

    const visibleSet = new Set(nextOrderedVisibleIds);
    let visibleIndex = 0;
    const resequenced = currentFullOrder.map((id) => {
      if (!visibleSet.has(id)) return id;
      const nextId = nextOrderedVisibleIds[visibleIndex];
      visibleIndex += 1;
      return nextId ?? id;
    });

    const seen = new Set(resequenced);
    for (const id of nextOrderedVisibleIds) {
      if (seen.has(id)) continue;
      resequenced.push(id);
      seen.add(id);
    }

    if (arraysEqual(currentFullOrder, resequenced)) return;

    setTimelineManualOrderByProject((previous) => ({
      ...previous,
      [timelineOrderKey]: resequenced,
    }));
    void persistTimelineOrder(resequenced);
  }, [
    timelineDefaultOrderIds,
    timelineManualOrderByProject,
    timelineOrderKey,
    persistTimelineOrder,
  ]);

  const timelineAssigneeOptions = React.useMemo<ReadonlyArray<GlassPicklistOption<string>>>(() => {
    const uniqueIds = new Set<string>();
    for (const ticket of timelineBaseTickets) {
      const assigneeUid = String(ticket.assignedAgentId ?? '').trim();
      if (!assigneeUid) continue;
      uniqueIds.add(assigneeUid);
    }

    const currentUid = String(user?.uid ?? '').trim();
    const sortedIds = Array.from(uniqueIds).sort((a, b) => {
      const aIsMe = currentUid && a === currentUid;
      const bIsMe = currentUid && b === currentUid;
      if (aIsMe && !bIsMe) return -1;
      if (bIsMe && !aIsMe) return 1;
      const aTicket = timelineBaseTickets.find((ticket) => String(ticket.assignedAgentId ?? '').trim() === a);
      const bTicket = timelineBaseTickets.find((ticket) => String(ticket.assignedAgentId ?? '').trim() === b);
      const aName = assigneeDirectory[a]?.name ?? resolveInternalAssigneeDisplay(aTicket as any, a);
      const bName = assigneeDirectory[b]?.name ?? resolveInternalAssigneeDisplay(bTicket as any, b);
      return aName.localeCompare(bName);
    });

    const mapped = sortedIds.map((uid) => {
      const ticket = timelineBaseTickets.find((entry) => String(entry.assignedAgentId ?? '').trim() === uid);
      const name = assigneeDirectory[uid]?.name ?? resolveInternalAssigneeDisplay(ticket as any, uid);
      const email = assigneeDirectory[uid]?.email ?? '';
      return {
        value: uid,
        label: email ? `${name} (${email})` : name,
      };
    });

    return [{ value: ALL_ASSIGNEES_VALUE, label: 'Team view' }, ...mapped];
  }, [timelineBaseTickets, user?.uid, assigneeDirectory]);

  React.useEffect(() => {
    if (timelineAssigneeOptions.some((option) => option.value === selectedAssigneeId)) return;
    setSelectedAssigneeId(ALL_ASSIGNEES_VALUE);
  }, [timelineAssigneeOptions, selectedAssigneeId]);

  const timelineTickets = React.useMemo(() => {
    if (selectedAssigneeId === ALL_ASSIGNEES_VALUE) return timelineBaseTickets;
    return timelineBaseTickets.filter(
      (ticket) => String(ticket.assignedAgentId ?? '').trim() === selectedAssigneeId,
    );
  }, [timelineBaseTickets, selectedAssigneeId]);

  const projectTimelineLanes = React.useMemo<TeamTimelineLane[]>(() => {
    return [...visibleProjects]
      .map((project) => {
        const projectTickets = projectTicketsById.get(project.id) ?? [];
        const statusSummary = buildProjectStatusSummary(project, projectTickets, viewerTimezone, null);
        const range = toProjectTimelineRange(project, projectTickets);
        const teamNodeId = safeText((project as any).teamNodeId);
        const teamLabel = teamNodeId ? (teamLabelByNodeId.get(teamNodeId) || teamNodeId) : 'Unassigned team';
        const ticketLabel =
          statusSummary.totalTickets === 1 ? '1 ticket' : `${statusSummary.totalTickets} tickets`;
        const milestoneLabel =
          statusSummary.totalMilestones === 1
            ? '1 milestone'
            : `${statusSummary.totalMilestones} milestones`;

        return {
          id: project.id,
          label: project.name,
          subtitle: `${teamLabel} - ${milestoneLabel} - ${ticketLabel}`,
          status: toProjectTimelineStatus(project, statusSummary, range),
          startAt: range.startAt,
          endAt: range.endAt,
          updatedAt:
            maybeTsToIso(project.updatedAt) ??
            maybeTsToIso(project.createdAt) ??
            range.endAt,
          openCount: statusSummary.remainingTickets,
          totalCount: statusSummary.totalTickets,
        };
      })
      .sort((a, b) => {
        const startDelta = new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
        if (startDelta !== 0) return startDelta;
        const endDelta = new Date(a.endAt).getTime() - new Date(b.endAt).getTime();
        if (endDelta !== 0) return endDelta;
        return a.label.localeCompare(b.label);
      });
  }, [projectTicketsById, teamLabelByNodeId, viewerTimezone, visibleProjects]);

  const timelineScopeLabel = selectedProject ? 'Tickets' : 'Programs';
  const timelineColumnLabel = selectedProject
    ? (selectedMilestoneRecord?.title || 'Tickets')
    : 'Programs';
  const ticketsPanelTitle = selectedMilestoneRecord?.title || undefined;
  const showClientMobileLayout = isClientWorkspace && isMobileProjectsLayout;

  const timelineLanes = React.useMemo<TeamTimelineLane[]>(() => {
    if (!selectedProject) return projectTimelineLanes;

    const activeOrderIds = timelineOrderKey
      ? (timelineManualOrderByProject[timelineOrderKey] ?? timelineDefaultOrderIds)
      : timelineDefaultOrderIds;
    const orderIndex = new Map(activeOrderIds.map((id, index) => [id, index]));

    return [...timelineTickets]
      .sort((a, b) => {
        const aIndex = orderIndex.get(a.id);
        const bIndex = orderIndex.get(b.id);
        if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
        if (aIndex !== undefined) return -1;
        if (bIndex !== undefined) return 1;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      })
      .map((aggregate) => {
        const assigneeUid = String(aggregate.assignedAgentId ?? '').trim();
        const assigneeLabel = assigneeUid
          ? (assigneeDirectory[assigneeUid]?.name ?? resolveInternalAssigneeDisplay(aggregate as any, assigneeUid))
          : 'Unassigned';
        const clientLabel = clientNameById.get(aggregate.clientId) || 'Client';
        const creatorLabel = resolveClientDisplayName(
          {
            firstName: aggregate.createdByFirstName,
            lastName: aggregate.createdByLastName,
            displayName: aggregate.createdByDisplayName,
            email: aggregate.createdByEmail,
            uid: aggregate.createdBy,
          },
          'Unknown creator',
        );
        const projectLabel = projectNameById.get(aggregate.projectId) || 'Unassigned';

        return {
          id: aggregate.id,
          label: aggregate.ticketNumber
            ? `#${aggregate.ticketNumber} ${aggregate.title || 'Untitled ticket'}`
            : aggregate.title || 'Untitled ticket',
          subtitle: `${assigneeLabel} - ${clientLabel} - ${creatorLabel} - ${projectLabel}`,
          status: aggregate.status,
          startAt: aggregate.startAt,
          endAt: aggregate.endAt,
          updatedAt: aggregate.updatedAt,
          openCount: isOpenLike(aggregate.status) ? 1 : 0,
          totalCount: 1,
        };
      });
  }, [
    projectTimelineLanes,
    selectedProject,
    timelineTickets,
    timelineOrderKey,
    timelineManualOrderByProject,
    timelineDefaultOrderIds,
    assigneeDirectory,
    clientNameById,
    projectNameById,
    isOpenLike,
  ]);

  const timelineEditable = Boolean(selectedProject);
  const timelineReorderable = timelineEditable;
  const selectedScopeLabel = React.useMemo(() => {
    if (isClientWorkspace) {
      if (!selectedOrganizationId) return null;
      return (
        organizationItems.find((item) => item.id === selectedOrganizationId)?.label ||
        organizationLabelById.get(selectedOrganizationId) ||
        selectedOrganizationId
      );
    }
    if (!selectedClient) return null;
    return (
      clientItems.find((item) => item.id === selectedClient)?.label ||
      organizationLabelById.get(selectedClient) ||
      selectedClient
    );
  }, [
    clientItems,
    isClientWorkspace,
    organizationItems,
    organizationLabelById,
    selectedClient,
    selectedOrganizationId,
  ]);
  const selectedTeamLabel = React.useMemo(() => {
    if (selectedTeamId === ALL_TEAMS_VALUE) return 'All teams';
    return teamOptions.find((option) => option.value === selectedTeamId)?.label ?? selectedTeamId;
  }, [selectedTeamId, teamOptions]);
  const projectsPanelContextLabel = React.useMemo(() => {
    if (!selectedScopeLabel) {
      return isClientWorkspace
        ? 'Select a workspace to view projects'
        : 'Select a client to view projects';
    }
    if (isClientWorkspace) return `For ${selectedScopeLabel}`;
    if (selectedTeamId === ALL_TEAMS_VALUE) return `For ${selectedScopeLabel} across all teams`;
    return `For ${selectedScopeLabel} on ${selectedTeamLabel}`;
  }, [isClientWorkspace, selectedScopeLabel, selectedTeamId, selectedTeamLabel]);

  const projectDetailsRecord = React.useMemo(
    () => (projectDetailsProjectId ? projectById(projectDetailsProjectId) : null),
    [projectById, projectDetailsProjectId],
  );

  const projectDetailsOrganizationLabel = React.useMemo(() => {
    if (!projectDetailsRecord) return null;
    const projectOrgId = String((projectDetailsRecord as any).orgId ?? '').trim();
    if (!projectOrgId) return selectedScopeLabel;
    return organizationLabelById.get(projectOrgId) || projectOrgId;
  }, [organizationLabelById, projectDetailsRecord, selectedScopeLabel]);

  const projectDetailsTeamLabel = React.useMemo(() => {
    if (!projectDetailsRecord) return null;
    if (!isClientWorkspace && selectedTeamId !== ALL_TEAMS_VALUE) {
      return selectedTeamLabel;
    }
    const teamNodeId = String((projectDetailsRecord as any).teamNodeId ?? '').trim();
    if (!teamNodeId) return selectedTeamId === ALL_TEAMS_VALUE ? null : selectedTeamLabel;
    return teamLabelByNodeId.get(teamNodeId) || teamNodeId;
  }, [isClientWorkspace, projectDetailsRecord, selectedTeamId, selectedTeamLabel, teamLabelByNodeId]);

  const projectDetailsTickets = React.useMemo(
    () =>
      projectDetailsRecord
        ? (projectTicketsById.get(projectDetailsRecord.id) ?? []).filter((ticket) =>
            isClientWorkspace || selectedTeamId === ALL_TEAMS_VALUE
              ? true
              : ticketBelongsToTeam(ticket, selectedTeamId),
          )
        : [],
    [isClientWorkspace, projectDetailsRecord, projectTicketsById, selectedTeamId],
  );

  const projectDetailsOrgId = React.useMemo(
    () => safeText((projectDetailsRecord as any)?.orgId) || null,
    [projectDetailsRecord],
  );

  const projectDetailsTeamNodeId = React.useMemo(
    () =>
      !isClientWorkspace && selectedTeamId !== ALL_TEAMS_VALUE
        ? selectedTeamId
        : safeText((projectDetailsRecord as any)?.teamNodeId) || null,
    [isClientWorkspace, projectDetailsRecord, selectedTeamId],
  );

  React.useEffect(() => {
    if (isClientWorkspace || !projectDetailsOrgId) return;
    if (forecastOrgDetailsByOrgId[projectDetailsOrgId] !== undefined) return;
    let mounted = true;
    (async () => {
      try {
        const response = await api.get(`/admin/organizations/${encodeURIComponent(projectDetailsOrgId)}`);
        if (!mounted) return;
        setForecastOrgDetailsByOrgId((previous) => ({
          ...previous,
          [projectDetailsOrgId]: normalizeProjectForecastOrgDetails(response?.data),
        }));
      } catch {
        if (!mounted) return;
        setForecastOrgDetailsByOrgId((previous) => ({
          ...previous,
          [projectDetailsOrgId]: null,
        }));
      }
    })();
    return () => {
      mounted = false;
    };
  }, [api, forecastOrgDetailsByOrgId, isClientWorkspace, projectDetailsOrgId]);

  React.useEffect(() => {
    if (isClientWorkspace || !projectDetailsTeamNodeId) return;
    if (teamCapacitySummaryByNodeId[projectDetailsTeamNodeId] !== undefined) return;
    let mounted = true;
    (async () => {
      try {
        const response = await api.get(
          `/projects/team-capacity-summary?teamNodeId=${encodeURIComponent(projectDetailsTeamNodeId)}`,
        );
        if (!mounted) return;
        setTeamCapacitySummaryByNodeId((previous) => ({
          ...previous,
          [projectDetailsTeamNodeId]:
            normalizeTeamCapacitySummaryPayload(response?.data),
        }));
      } catch {
        if (!mounted) return;
        setTeamCapacitySummaryByNodeId((previous) => ({
          ...previous,
          [projectDetailsTeamNodeId]: null,
        }));
      }
    })();
    return () => {
      mounted = false;
    };
  }, [api, isClientWorkspace, projectDetailsTeamNodeId, teamCapacitySummaryByNodeId]);

  const projectDetailsOrgForecast = React.useMemo(
    () => (projectDetailsOrgId ? forecastOrgDetailsByOrgId[projectDetailsOrgId] ?? null : null),
    [forecastOrgDetailsByOrgId, projectDetailsOrgId],
  );

  const projectDetailsTeamCapacitySummary = React.useMemo(
    () =>
      projectDetailsTeamNodeId
        ? teamCapacitySummaryByNodeId[projectDetailsTeamNodeId] ?? null
        : null,
    [projectDetailsTeamNodeId, teamCapacitySummaryByNodeId],
  );

  const projectDetailsServiceFamily = React.useMemo(() => {
    const counts = new Map<ForecastServiceFamily, number>();
    projectDetailsTickets.forEach((ticket) => {
      const family = normalizeForecastServiceFamily(ticket.serviceCode);
      if (!family) return;
      counts.set(family, (counts.get(family) ?? 0) + 1);
    });
    const dominant = Array.from(counts.entries()).sort((left, right) => right[1] - left[1])[0]?.[0];
    return dominant ?? null;
  }, [projectDetailsTickets]);

  const projectDetailsServiceContract = React.useMemo(() => {
    if (!projectDetailsOrgForecast) return null;
    if (!projectDetailsServiceFamily) return null;
    return (
      projectDetailsOrgForecast.serviceContracts.find(
        (contract) => inferForecastServiceFamilyFromContract(contract) === projectDetailsServiceFamily,
      ) ?? null
    );
  }, [projectDetailsOrgForecast, projectDetailsServiceFamily]);

  const projectDetailsForecastContext = React.useMemo<ProjectCapacityForecastContext | null>(() => {
    if (!projectDetailsRecord) return null;

    const contract = projectDetailsServiceContract;
    const accountHealth = projectDetailsOrgForecast?.accountHealth ?? null;

    return {
      team: projectDetailsTeamCapacitySummary
        ? {
            capacityHoursPerContributor: projectDetailsTeamCapacitySummary.teamCapacityHours,
            contributorCount: projectDetailsTeamCapacitySummary.contributorCount,
            queuedMinutes: projectDetailsTeamCapacitySummary.queuedMinutes,
            assignedMinutes: projectDetailsTeamCapacitySummary.assignedMinutes,
            unassignedMinutes: projectDetailsTeamCapacitySummary.unassignedMinutes,
          }
        : null,
      contract:
        contract || accountHealth
          ? {
              serviceLabel:
                contract?.serviceName ??
                (projectDetailsServiceFamily === 'marketing'
                  ? 'Managed Marketing'
                  : projectDetailsServiceFamily === 'platform'
                    ? 'Platform Development'
                    : projectDetailsServiceFamily === 'assets_design'
                      ? 'Assets/Design'
                      : null),
              contractedMinutesMin:
                contract?.contractedMonthlyMinutesMin ??
                accountHealth?.contractedMonthlyMinutesMin ??
                null,
              contractedMinutesMax:
                contract?.contractedMonthlyMinutesMax ??
                accountHealth?.contractedMonthlyMinutesMax ??
                null,
              remainingBillableMinutesMax:
                contract?.remainingBillableMinutesMax ??
                accountHealth?.remainingBillableMinutesMax ??
                null,
              plannedRemainingEstimateMinutes:
                contract?.plannedRemainingEstimateMinutes ??
                accountHealth?.plannedRemainingEstimateMinutes ??
                null,
            }
          : null,
    };
  }, [
    projectDetailsOrgForecast,
    projectDetailsRecord,
    projectDetailsServiceContract,
    projectDetailsServiceFamily,
    projectDetailsTeamCapacitySummary,
  ]);

  const projectDetailsStatusSummary = React.useMemo(
    () =>
      projectDetailsRecord
        ? buildProjectStatusSummary(
            projectDetailsRecord,
            projectDetailsTickets,
            viewerTimezone,
            projectDetailsForecastContext,
          )
        : null,
    [
      projectDetailsForecastContext,
      projectDetailsRecord,
      projectDetailsTickets,
      viewerTimezone,
    ],
  );

  const openMilestoneComposer = React.useCallback((
    projectId: string,
    position: ProjectMilestoneComposer['position'],
  ) => {
    setProjectMilestoneComposerByProject((previous) => ({
      ...previous,
      [projectId]: {
        position,
        value: previous[projectId]?.value ?? '',
      },
    }));
  }, []);

  const updateMilestoneComposer = React.useCallback((projectId: string, value: string) => {
    setProjectMilestoneComposerByProject((previous) => ({
      ...previous,
      [projectId]: {
        position: previous[projectId]?.position ?? 'top',
        value,
      },
    }));
  }, []);

  const discardMilestoneComposer = React.useCallback((projectId: string) => {
    setProjectMilestoneComposerByProject((previous) => {
      if (!previous[projectId]) return previous;
      const next = { ...previous };
      delete next[projectId];
      return next;
    });
  }, []);

  const commitMilestoneComposer = React.useCallback(async (projectId: string, rawValue: string) => {
    const nextTitle = rawValue.trim();
    discardMilestoneComposer(projectId);
    if (!nextTitle) return;
    await dispatch(createProjectMilestone({ projectId, title: nextTitle }));
  }, [discardMilestoneComposer, dispatch]);

  const handleProjectMilestoneDateChange = React.useCallback(async (
    projectId: string,
    milestoneId: string,
    patch: { startDate?: string | null; endDate?: string | null },
  ) => {
    await dispatch(updateProjectMilestone({
      projectId,
      milestoneId,
      patch: {
        ...patch,
        timezone: viewerTimezone,
      },
    }));
  }, [dispatch, viewerTimezone]);

  const handleProjectDetailsSave = React.useCallback(async (
    projectId: string,
    patch: {
      brief?: string | null;
      definitionOfDone?: string | null;
      stakeholderNotes?: string | null;
      operatingRules?: string | null;
      duplicatePreventionRules?: string | null;
      startDate?: string | null;
      endDate?: string | null;
    },
  ) => {
    await dispatch(updateProject({
      projectId,
      patch: {
        ...patch,
        timezone: viewerTimezone,
      },
    })).unwrap();
  }, [dispatch, viewerTimezone]);

  const handleProjectMilestoneDetailsSave = React.useCallback(async (
    projectId: string,
    milestoneId: string,
    patch: {
      title?: string;
      outcome?: string | null;
      definitionOfDone?: string | null;
      notesBlockers?: string | null;
      startDate?: string | null;
      endDate?: string | null;
    },
  ) => {
    await dispatch(updateProjectMilestone({
      projectId,
      milestoneId,
      patch: {
        ...patch,
        timezone: viewerTimezone,
      },
    })).unwrap();
  }, [dispatch, viewerTimezone]);

  const openMilestoneDatePicker = React.useCallback((refKey: string) => {
    const input = milestoneDateInputRefs.current[refKey];
    if (!input) return;
    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }
    input.focus();
    input.click();
  }, []);

  const renderProjectMilestoneAccordion = React.useCallback((item: TeamFilterItem) => {
    const milestones = (((projectById(item.id) as any)?.milestones ?? []) as Array<{ id: string; title: string; plannedStartAt?: string | null; plannedEndAt?: string | null; timezone?: string | null }>);
    const composer = projectMilestoneComposerByProject[item.id] ?? null;
    const showComposer = Boolean(composer);
    const renderComposer = (position: ProjectMilestoneComposer['position']) => {
      if (!composer || composer.position !== position) return null;
      return (
        <ProjectMilestoneInput
          autoFocus
          value={composer.value}
          onChange={(event) => updateMilestoneComposer(item.id, event.target.value)}
          onBlur={(event) => void commitMilestoneComposer(item.id, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void commitMilestoneComposer(item.id, (event.target as HTMLInputElement).value);
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              discardMilestoneComposer(item.id);
            }
          }}
          placeholder="Name the milestone or sprint"
          aria-label={`New milestone for ${item.label}`}
        />
      );
    };

    return (
      <ProjectMilestoneAccordion>
        {milestones.length > 0 ? (
          <>
            <ProjectMilestoneAction
              type="button"
              onClick={() => openMilestoneComposer(item.id, 'top')}
            >
              + milestone
            </ProjectMilestoneAction>
            {renderComposer('top')}
            <ProjectMilestoneList>
              {milestones.map((milestone) => {
                const childTicketCount = uiTickets.filter(
                  (ticket) =>
                    String(ticket.projectId ?? '').trim() === item.id &&
                    String(ticket.milestoneId ?? '').trim() === milestone.id,
                ).length;

                return (
                  <ProjectMilestoneRow
                    key={milestone.id}
                    $active={selectedMilestoneId === milestone.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleMilestoneSelect(item.id, milestone.id)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      handleMilestoneSelect(item.id, milestone.id);
                    }}
                  >
                    <ProjectMilestoneHeader>
                      <ProjectMilestoneHeaderBody>
                        <ProjectMilestoneTitle>{milestone.title}</ProjectMilestoneTitle>
                        <ProjectMilestoneMeta>
                          <ProjectMilestoneDateTrigger
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openMilestoneDatePicker(`${milestone.id}:start`);
                            }}
                          >
                            From {formatMilestoneDateLabel(milestone.plannedStartAt, milestone.timezone, '--/--')}
                          </ProjectMilestoneDateTrigger>
                          <ProjectMilestoneDateSeparator>-</ProjectMilestoneDateSeparator>
                          <ProjectMilestoneDateTrigger
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openMilestoneDatePicker(`${milestone.id}:end`);
                            }}
                          >
                            To {formatMilestoneDateLabel(milestone.plannedEndAt, milestone.timezone, '--/--')}
                          </ProjectMilestoneDateTrigger>
                        </ProjectMilestoneMeta>
                      </ProjectMilestoneHeaderBody>

                      <ProjectMilestoneActions data-milestone-menu-root="true">
                        <ProjectMilestoneMenuButton
                          type="button"
                          aria-label={`Milestone actions for ${milestone.title}`}
                          title={`Milestone actions for ${milestone.title}`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            const rect = event.currentTarget.getBoundingClientRect();
                            setOpenMilestoneMenu((current) => {
                              if (current?.milestoneId === milestone.id) return null;
                              return {
                                projectId: item.id,
                                milestoneId: milestone.id,
                                milestoneTitle: milestone.title,
                                childTicketCount,
                                left: Math.min(window.innerWidth - 12, rect.right),
                                top: resolveMilestoneMenuPosition(rect) === 'top'
                                  ? rect.top - 6
                                  : rect.bottom + 6,
                                placement: resolveMilestoneMenuPosition(rect),
                              };
                            });
                          }}
                        >
                          ...
                        </ProjectMilestoneMenuButton>
                      </ProjectMilestoneActions>
                    </ProjectMilestoneHeader>

                    <HiddenMilestoneDateInput
                      ref={(node) => {
                        milestoneDateInputRefs.current[`${milestone.id}:start`] = node;
                      }}
                      value={toLocalDateInputFromIsoInTimezone(milestone.plannedStartAt, milestone.timezone)}
                      onChange={(event) => {
                        void handleProjectMilestoneDateChange(item.id, milestone.id, { startDate: event.target.value || null });
                      }}
                      aria-label={`Start date for ${milestone.title}`}
                    />
                    <HiddenMilestoneDateInput
                      ref={(node) => {
                        milestoneDateInputRefs.current[`${milestone.id}:end`] = node;
                      }}
                      value={toLocalDateInputFromIsoInTimezone(milestone.plannedEndAt, milestone.timezone)}
                      onChange={(event) => {
                        void handleProjectMilestoneDateChange(item.id, milestone.id, { endDate: event.target.value || null });
                      }}
                      aria-label={`End date for ${milestone.title}`}
                    />
                  </ProjectMilestoneRow>
                );
              })}
            </ProjectMilestoneList>
            {renderComposer('bottom')}
          </>
        ) : (
          <>
            {showComposer ? renderComposer('empty') : (
              <ProjectMilestoneEmpty
                type="button"
                onClick={() => openMilestoneComposer(item.id, 'empty')}
              >
                create first milestone
              </ProjectMilestoneEmpty>
            )}
          </>
        )}
      </ProjectMilestoneAccordion>
    );
  }, [
    commitMilestoneComposer,
    discardMilestoneComposer,
    handleProjectMilestoneDateChange,
    handleMilestoneSelect,
    openMilestoneDatePicker,
    openMilestoneComposer,
    projectById,
    projectMilestoneComposerByProject,
    selectedMilestoneId,
    uiTickets,
    updateMilestoneComposer,
  ]);

  const handleTimelineLaneDateChange = async (
    laneId: string,
    nextRange: { startAt: string; endAt: string },
  ) => {
    if (!selectedProject) return;
    const startDate = toLocalDateInputFromIso(nextRange.startAt);
    const dueDate = toLocalDateInputFromIso(nextRange.endAt);
    await handleUpdate(laneId, { startDate, dueDate });
  };

  const createTicketContext = React.useMemo(() => {
    const selectedProjectRecord = selectedProject ? (projectById(selectedProject) as any) : null;

    const clientIdForCreate = String(
      isClientWorkspace
        ? selectedProjectRecord?.clientId || selectedProjectRecord?.ownerUid || user?.uid || ''
        : workspaceRepresentativeClientIdByWorkspace.get(selectedClient ?? '') || selectedProjectRecord?.clientId || selectedProjectRecord?.ownerUid || '',
    ).trim();

    if (!clientIdForCreate) return null;

    const clientRecord = clientUserById.get(clientIdForCreate);
    const fallbackDisplay = resolveClientDisplayName(
      {
        displayName: (user as any)?.displayName ?? (user as any)?.name ?? null,
        email: user?.email ?? null,
        uid: user?.uid ?? null,
      },
      clientIdForCreate,
    );

    return {
      id: '__create_context__',
      title: '',
      description: '',
      orgId: selectedProjectRecord?.orgId ?? selectedOrganizationId ?? orgId ?? null,
      projectId: selectedProjectRecord?.id ?? '',
      milestoneId: selectedMilestoneId ?? null,
      milestoneTitle: selectedMilestoneRecord?.title ?? null,
      priority: 'medium',
      assignedAgentId: null,
      assignedOrgMemberId: null,
      owningTeamNodeId: !isClientWorkspace && selectedTeamId !== ALL_TEAMS_VALUE ? selectedTeamId : null,
      offeringId: null,
      estimatedMinutes: null,
      clientId: clientIdForCreate,
      clientDisplayName:
        clientNameById.get(clientIdForCreate) ||
        clientRecord?.name ||
        fallbackDisplay,
      clientEmail: clientRecord?.email ?? null,
      subtasks: [],
      participants: [],
    } as any;
  }, [
    clientNameById,
    clientUserById,
    isClientWorkspace,
    orgId,
    projectById,
    selectedClient,
    selectedMilestoneId,
    selectedMilestoneRecord?.title,
    selectedOrganizationId,
    selectedProject,
    selectedTeamId,
    user,
    workspaceRepresentativeClientIdByWorkspace,
  ]);

  const canConfirmDeleteMilestone = React.useMemo(() => {
    if (!deleteMilestoneDialog) return false;
    const confirmWordMatches = deleteMilestoneConfirmWord.trim().toUpperCase() === 'DELETE';
    if (!confirmWordMatches) return false;
    if (deleteMilestoneDialog.childTicketCount <= 0) return true;
    return Boolean(deleteMilestoneHandling);
  }, [deleteMilestoneConfirmWord, deleteMilestoneDialog, deleteMilestoneHandling]);

  const canConfirmDeleteProject = React.useMemo(() => {
    if (!deleteProjectDialog) return false;
    const confirmWordMatches = deleteProjectConfirmWord.trim().toUpperCase() === 'DELETE';
    if (!confirmWordMatches) return false;
    if (deleteProjectDialog.childTicketCount <= 0) return true;
    return Boolean(deleteProjectHandling);
  }, [deleteProjectConfirmWord, deleteProjectDialog, deleteProjectHandling]);

  if (isClientWorkspace && !clientWorkspaceReady) {
    const gateTitle = isSubActive
      ? 'Complete your business profile first'
      : 'An active service is required first';
    const gateHeroTitle = isSubActive
      ? 'Complete Your Business Profile'
      : 'Activate A Service First';
    const gateBody = isSubActive
      ? 'This organization is not ready for projects, programs, or tickets yet. Complete and save the business profile before work can be created in the client workspace.'
      : 'This organization needs an active service contract before projects, programs, or tickets can be created in the client workspace.';

    return (
      <Shell>
        <WorkspaceGateCard>
          <WorkspaceGateHero>
            <WorkspaceGateHeroOverlay />
            <WorkspaceGateLottieWrap aria-hidden="true">
              <Lottie animationData={spacemailAnimation} loop autoplay />
            </WorkspaceGateLottieWrap>
            <WorkspaceGateHeroTitle>{gateHeroTitle}</WorkspaceGateHeroTitle>
          </WorkspaceGateHero>
          <WorkspaceGateBottom>
            <WorkspaceGateTitle>{gateTitle}</WorkspaceGateTitle>
            <WorkspaceGateBody>{gateBody}</WorkspaceGateBody>
            <WorkspaceGateActions>
              <WorkspaceGateCta
                type="button"
                onClick={() => navigate('/dashboard/business')}
              >
                Open business page
              </WorkspaceGateCta>
            </WorkspaceGateActions>
          </WorkspaceGateBottom>
        </WorkspaceGateCard>
      </Shell>
    );
  }

  return (
    <Shell>
      <Layout>
        {showClientMobileLayout ? (
          <MobileWorkspaceLayout>
            <MobileFiltersCard>
              <MobileFiltersGrid>
                <MobileFilterField>
                  <MobileFilterLabel>Workspace</MobileFilterLabel>
                  <MobilePicklistControl>
                    <GlassPicklist<string>
                      value={selectedOrganizationId ?? mobileWorkspaceOptions[0]?.value ?? ''}
                      options={mobileWorkspaceOptions}
                      onChange={setSelectedOrganizationId}
                      ariaLabel="Filter by workspace"
                      searchable={mobileWorkspaceOptions.length > 6}
                      searchPlaceholder="Search workspaces..."
                      emptyLabel="No workspaces available."
                    />
                  </MobilePicklistControl>
                </MobileFilterField>

                <MobileFilterField>
                  <MobileFilterLabel>Project</MobileFilterLabel>
                  <MobilePicklistControl>
                    <GlassPicklist<string>
                      value={selectedProject ?? ALL_PROJECTS_MOBILE_VALUE}
                      options={mobileProjectOptions}
                      onChange={(value) =>
                        handleProjectSelect(
                          value === ALL_PROJECTS_MOBILE_VALUE ? null : value,
                        )
                      }
                      ariaLabel="Filter by project"
                      searchable={mobileProjectOptions.length > 7}
                      searchPlaceholder="Search projects..."
                      emptyLabel="No projects available."
                    />
                  </MobilePicklistControl>
                </MobileFilterField>
              </MobileFiltersGrid>
            </MobileFiltersCard>

            <MobileTicketsArea>
              <TicketsTable
                tickets={ticketsTableRows as any}
                projects={projects as any}
                projectId={selectedProject || 'all'}
                title={ticketsPanelTitle}
                onOpen={openEdit}
                onCreate={selectedProject && clientCanCreateWork ? openCreate : undefined}
                height="100%"
                loading={ticketsLoading}
                mobileMode="table"
              />
            </MobileTicketsArea>
          </MobileWorkspaceLayout>
        ) : (
          <>
            <TopRow>
              <ClientsArea $showTeamPanel={!isClientWorkspace}>
                {!isClientWorkspace ? (
                  <TeamPanel>
                    <TeamPanelTitle>Team</TeamPanelTitle>
                    <TeamPicklistControl>
                      <GlassPicklist<string>
                        value={selectedTeamId}
                        options={teamOptions}
                        onChange={setSelectedTeamId}
                        ariaLabel="Select team"
                      />
                    </TeamPicklistControl>
                  </TeamPanel>
                ) : null}

                <ClientsPanelWrap>
                  <TeamFilterPanel
                    title={isClientWorkspace ? 'Workspaces' : 'Clients'}
                    items={isClientWorkspace ? organizationItems : clientItems}
                    selectedId={isClientWorkspace ? selectedOrganizationId : selectedClient}
                    onSelect={isClientWorkspace ? setSelectedOrganizationId : setSelectedClient}
                    searchPlaceholder={isClientWorkspace ? 'Search workspaces...' : 'Search clients...'}
                    sortLabel="Sort"
                    panelHeight="100%"
                    sortVariant="icon"
                  />
                </ClientsPanelWrap>
              </ClientsArea>

              <TimelineArea>
                <TeamTimelineCard
                  lanes={timelineLanes}
                  scopeLabel={timelineScopeLabel}
                  columnLabel={timelineColumnLabel}
                  height="100%"
                  editable={timelineEditable}
                  onLaneDateChange={timelineEditable ? handleTimelineLaneDateChange : undefined}
                  onLaneOrderChange={timelineReorderable ? handleTimelineLaneOrderChange : undefined}
                  onCreateAtDate={
                    timelineEditable && clientCanCreateWork
                      ? (dateIso) => openCreate(dateIso)
                      : undefined
                  }
                  secondaryPicklist={
                    selectedProject
                      ? {
                          value: selectedAssigneeId,
                          options: timelineAssigneeOptions,
                          onChange: setSelectedAssigneeId,
                          ariaLabel: 'Filter tickets by assignee',
                          searchable: true,
                          searchPlaceholder: 'Search team members...',
                          emptyLabel: 'No assignees in this project yet.',
                        }
                      : undefined
                  }
                />
              </TimelineArea>
            </TopRow>

            <BottomRow>
              <ProjectsArea>
                <TeamFilterPanel
                  title="Projects"
                  items={projectItems}
                  selectedId={selectedProject}
                  onSelect={handleProjectSelect}
                  allowDeselect
                  searchPlaceholder="Search projects..."
                  sortLabel="Sort"
                  panelHeight="100%"
                  sortVariant="icon"
                  metaMode="stacked"
                  isItemExpandable={() => true}
                  expandedItemId={expandedProjectId}
                  onExpandedItemChange={setExpandedProjectId}
                  renderExpandedContent={renderProjectMilestoneAccordion}
                  contextLabel={
                    projectsPanelContextLabel
                  }
                  getItemMenuOptions={(item) => {
                    if (!canDeleteProjects) return [];
                    return [
                      {
                        id: 'duplicate',
                        label: 'Duplicate',
                        onSelect: () => {
                          setDuplicateProjectDialog({
                            projectId: item.id,
                            projectLabel: item.label,
                          });
                        },
                      },
                      {
                        id: 'delete',
                        label: 'Delete',
                        danger: true,
                        onSelect: () => {
                          void handleDeleteProject(item);
                        },
                      },
                    ];
                  }}
                  getItemInlineActions={(item) => {
                    return [
                      {
                        id: 'open',
                        label: 'Open',
                        ariaLabel: `Open ${item.label} project details`,
                        title: `Open ${item.label} project details`,
                        onClick: () => {
                          handleProjectSelect(item.id);
                          setProjectDetailsProjectId(item.id);
                        },
                      },
                    ];
                  }}
                  actionButton={{
                    icon: '+',
                    ariaLabel: isClientWorkspace
                      ? 'Create project for selected workspace'
                      : 'Create project for selected client',
                    title: selectedScopeLabel
                      ? `Create project for ${selectedScopeLabel}`
                      : isClientWorkspace
                        ? (isPaid
                            ? 'Select a workspace first'
                            : 'Active subscription required to create projects')
                        : 'Select a client first',
                    disabled: isClientWorkspace
                      ? !selectedOrganizationId || !isPaid
                      : !selectedClient,
                    onClick: openCreateProject,
                  }}
                />
              </ProjectsArea>

              <TableArea>
                <TicketsTable
                  tickets={ticketsTableRows as any}
                  projects={projects as any}
                  projectId={selectedProject || 'all'}
                  title={ticketsPanelTitle}
                  onOpen={openEdit}
                  onCreate={selectedProject && clientCanCreateWork ? openCreate : undefined}
                  height="100%"
                  loading={ticketsLoading}
                />
              </TableArea>
            </BottomRow>
          </>
        )}
      </Layout>

      <CreateProjectModal
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
        onCreate={handleCreateProject}
        nameOnly
      />

      <CreateProjectModal
        open={Boolean(duplicateProjectDialog)}
        onClose={() => setDuplicateProjectDialog(null)}
        onCreate={(payload) => {
          if (!duplicateProjectDialog) return;
          return handleDuplicateProject({
            projectId: duplicateProjectDialog.projectId,
            name: payload.name,
          });
        }}
        nameOnly
        title="Duplicate project"
        submitLabel="Duplicate project"
        initialName={duplicateProjectDialog ? `${duplicateProjectDialog.projectLabel} copy` : ''}
      />

      <TicketEditorDrawer
        open={editorOpen}
        mode="create"
        onClose={() => {
          setEditorOpen(false);
          setCreateSeedRange(null);
        }}
        project={selectedProject ? (projectById(selectedProject) as any) : undefined}
        projects={projects as any}
        ticket={createTicketContext ?? undefined}
        agents={[]}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />

      <TicketEditorDrawer
        open={Boolean(isClientWorkspace && clientEditTicketId && clientEditorTicket)}
        mode="edit"
        onClose={() => setClientEditTicketId(null)}
        project={clientEditingProject as any}
        projects={projects as any}
        ticket={clientEditorTicket ?? undefined}
        agents={[]}
        onCreate={async () => ''}
        onUpdate={handleUpdate}
      />

      <ProjectDetailsDrawer
        open={Boolean(projectDetailsRecord)}
        project={projectDetailsRecord as any}
        organizationLabel={projectDetailsOrganizationLabel}
        teamLabel={projectDetailsTeamLabel}
        tickets={projectDetailsTickets as any}
        viewerTimezone={viewerTimezone}
        statusSummary={projectDetailsStatusSummary}
        onSaveProject={(patch) => {
          if (!projectDetailsRecord) return;
          return handleProjectDetailsSave(projectDetailsRecord.id, patch);
        }}
        onSaveMilestone={(milestoneId, patch) => {
          if (!projectDetailsRecord) return;
          return handleProjectMilestoneDetailsSave(projectDetailsRecord.id, milestoneId, patch);
        }}
        onClose={() => setProjectDetailsProjectId(null)}
      />

      {openMilestoneMenu
        ? createPortal(
            <ProjectMilestoneMenu
              data-milestone-menu-root="true"
              $left={openMilestoneMenu.left}
              $top={openMilestoneMenu.top}
              $placement={openMilestoneMenu.placement}
            >
              <ProjectMilestoneMenuItem
                type="button"
                $danger
                onClick={() => {
                  openDeleteMilestoneDialog(
                    openMilestoneMenu.projectId,
                    openMilestoneMenu.milestoneId,
                    openMilestoneMenu.milestoneTitle,
                    openMilestoneMenu.childTicketCount,
                  );
                }}
              >
                Delete milestone
              </ProjectMilestoneMenuItem>
            </ProjectMilestoneMenu>,
            document.body,
          )
        : null}

      {deleteMilestoneDialog ? (
        <ConfirmOverlay
          role="presentation"
          onMouseDown={(event) => {
            if (event.target !== event.currentTarget) return;
            closeDeleteMilestoneDialog();
          }}
        >
          <ConfirmDialog role="dialog" aria-modal="true" aria-labelledby="delete-milestone-title">
            <ConfirmHeader>
              <ConfirmTitle id="delete-milestone-title">Delete milestone</ConfirmTitle>
              <ConfirmBody>
                Delete <strong>{deleteMilestoneDialog.milestoneTitle}</strong>.
                {deleteMilestoneDialog.childTicketCount > 0
                  ? ` This milestone currently contains ${deleteMilestoneDialog.childTicketCount} child ticket${deleteMilestoneDialog.childTicketCount === 1 ? '' : 's'}.`
                  : ' This milestone does not currently contain any child tickets.'}
              </ConfirmBody>
            </ConfirmHeader>

            {deleteMilestoneDialog.childTicketCount > 0 ? (
              <ConfirmOptions>
                <ConfirmOption
                  type="button"
                  $active={deleteMilestoneHandling === 'transfer_to_project'}
                  onClick={() => setDeleteMilestoneHandling('transfer_to_project')}
                >
                  <ConfirmOptionTitle>Transfer child tickets to the parent project</ConfirmOptionTitle>
                  <ConfirmOptionMeta>Keep the tickets in the project and clear only their milestone assignment.</ConfirmOptionMeta>
                </ConfirmOption>
                <ConfirmOption
                  type="button"
                  $active={deleteMilestoneHandling === 'delete_tickets'}
                  $danger
                  onClick={() => setDeleteMilestoneHandling('delete_tickets')}
                >
                  <ConfirmOptionTitle>Delete all child tickets in this milestone</ConfirmOptionTitle>
                  <ConfirmOptionMeta>Permanently remove the milestone and every ticket currently assigned to it.</ConfirmOptionMeta>
                </ConfirmOption>
              </ConfirmOptions>
            ) : null}

            <ConfirmInputLabel>
              Type <strong>DELETE</strong> to confirm
              <ConfirmInput
                value={deleteMilestoneConfirmWord}
                onChange={(event) => setDeleteMilestoneConfirmWord(event.target.value)}
                placeholder="DELETE"
                autoFocus
              />
            </ConfirmInputLabel>

            <ConfirmActions>
              <ConfirmButton type="button" onClick={closeDeleteMilestoneDialog} disabled={deletingMilestone}>
                Cancel
              </ConfirmButton>
              <ConfirmButton
                type="button"
                $danger
                onClick={() => void handleConfirmDeleteMilestone()}
                disabled={!canConfirmDeleteMilestone || deletingMilestone}
              >
                {deletingMilestone ? 'Deleting...' : 'Delete milestone'}
              </ConfirmButton>
            </ConfirmActions>
          </ConfirmDialog>
        </ConfirmOverlay>
      ) : null}

      {deleteProjectDialog ? (
        <ConfirmOverlay
          role="presentation"
          onMouseDown={(event) => {
            if (event.target !== event.currentTarget) return;
            closeDeleteProjectDialog();
          }}
        >
          <ConfirmDialog role="dialog" aria-modal="true" aria-labelledby="delete-project-title">
            <ConfirmHeader>
              <ConfirmTitle id="delete-project-title">Delete project</ConfirmTitle>
              <ConfirmBody>
                Delete <strong>{deleteProjectDialog.projectTitle}</strong>.
                {deleteProjectDialog.childTicketCount > 0
                  ? ` This project currently contains ${deleteProjectDialog.childTicketCount} ticket${deleteProjectDialog.childTicketCount === 1 ? '' : 's'}.`
                  : ' This project does not currently contain any tickets.'}
              </ConfirmBody>
            </ConfirmHeader>

            {deleteProjectDialog.childTicketCount > 0 ? (
              <ConfirmOptions>
                <ConfirmOption
                  type="button"
                  $active={deleteProjectHandling === 'keep_tickets'}
                  onClick={() => setDeleteProjectHandling('keep_tickets')}
                >
                  <ConfirmOptionTitle>Keep tickets outside of the project</ConfirmOptionTitle>
                  <ConfirmOptionMeta>Delete the project, clear project and milestone assignment, and leave each ticket available in the client ticket pool.</ConfirmOptionMeta>
                </ConfirmOption>
                <ConfirmOption
                  type="button"
                  $active={deleteProjectHandling === 'delete_tickets'}
                  $danger
                  onClick={() => setDeleteProjectHandling('delete_tickets')}
                >
                  <ConfirmOptionTitle>Delete all tickets in this project</ConfirmOptionTitle>
                  <ConfirmOptionMeta>Permanently remove the project and every ticket currently assigned to it.</ConfirmOptionMeta>
                </ConfirmOption>
              </ConfirmOptions>
            ) : null}

            <ConfirmInputLabel>
              Type <strong>DELETE</strong> to confirm
              <ConfirmInput
                value={deleteProjectConfirmWord}
                onChange={(event) => setDeleteProjectConfirmWord(event.target.value)}
                placeholder="DELETE"
                autoFocus
              />
            </ConfirmInputLabel>

            <ConfirmActions>
              <ConfirmButton type="button" onClick={closeDeleteProjectDialog} disabled={deletingProject}>
                Cancel
              </ConfirmButton>
              <ConfirmButton
                type="button"
                $danger
                onClick={() => void handleConfirmDeleteProject()}
                disabled={!canConfirmDeleteProject || deletingProject}
              >
                {deletingProject ? 'Deleting...' : 'Delete project'}
              </ConfirmButton>
            </ConfirmActions>
          </ConfirmDialog>
        </ConfirmOverlay>
      ) : null}
    </Shell>
  );
}
